/**
 * Studio Kortuncan - sinematik giriş yolculuğu.
 *
 * Bu dosya, referans olarak verilen aboutluca.com deneyiminin çözümlenmiş
 * mimarisini birebir takip eder. Oradaki yapı şuydu:
 *
 *   - Sayfanın kendisi neredeyse boş: tam ekran sabitlenmiş tek bir WebGL
 *     tuvali + arkasında sadece kaydırma boşluğu yaratan "step" blokları.
 *   - Sahnedeki HER ŞEY (başlıklar dahil) WebGL içinde çizilir; DOM'da metin
 *     katmanı yoktur. Yazılar dünya uzayında durur, kamera onlara doğru uçar.
 *   - Kamera bir CatmullRom eğrisi üzerinde kapalı bir tur atar; yaw/pitch
 *     rig'i fare ile hafifçe salınır.
 *   - Kum tanesi bulutu tek bir ShaderMaterial'dir. Vertex shader sırayla
 *     dört efekt uygular: morph (düz düzlemden kum tepelerine), aynalama
 *     (split mirror), kara delik tüneli ve fare itmesi.
 *   - Fragment shader her tanecik için core + halo + edge maskesi üretip
 *     sahte bir ışıklandırma ekler; additive harmanlama ile parlar.
 *   - En sonda bloom + tone mapping geçişi tüm ışıkları yayar.
 *
 * Buradaki uygulama aynı zinciri kurar. Referansta kum tepeleri bir GLB
 * mesh'inden örnekleniyordu; elimizde o dosya olmadığı için aynı yükseklik
 * alanını CPU'da fbm gürültüsüyle üretiyoruz (şekil formülü referansla
 * aynı: y = -2.2 * (gürültü + rastgele + 0.4)).
 */
(() => {
  'use strict';

  const root = document.querySelector('[data-cinematic-intro]');
  if (!root) return;

  const utils = window.ThreeSceneUtils;
  const isMobile = window.matchMedia('(max-width: 749px)').matches;
  const isLowEnd = (navigator.deviceMemory && navigator.deviceMemory <= 4) || (navigator.hardwareConcurrency || 8) <= 4;
  const reduceMotion = utils ? utils.prefersReducedMotion() : true;
  const hasWebGL = utils ? utils.supportsWebGL() : false;
  const enhanced = !!utils && !reduceMotion && hasWebGL;

  const track = root.querySelector('[data-cine-track]');
  const stage = root.querySelector('[data-cine-stage]');
  const canvasMount = root.querySelector('[data-cine-canvas]');
  const loader = root.querySelector('[data-cine-loader]');
  const enterBtn = root.querySelector('[data-cine-enter]');
  const galleryMount = root.querySelector('[data-cine-gallery]');
  const trailCanvas = root.querySelector('[data-cine-trail]');

  // ---------------------------------------------------------------------
  // Küçük yardımcılar
  // ---------------------------------------------------------------------
  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
  const clamp01 = (v) => clamp(v, 0, 1);
  const lerp = (a, b, t) => a + (b - a) * t;
  const smoothstep = (e0, e1, x) => {
    const t = clamp01((x - e0) / (e1 - e0 || 1e-6));
    return t * t * (3 - 2 * t);
  };
  const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

  if (!enhanced) {
    root.classList.add('cine--static');
    return;
  }

  root.classList.add('cine--enhanced', 'cine--loading');

  // ---------------------------------------------------------------------
  // Ayarlar
  // ---------------------------------------------------------------------
  const css = (name, fallback) => {
    const v = getComputedStyle(root).getPropertyValue(name).trim();
    return v || fallback;
  };

  const QUALITY = isMobile || isLowEnd ? 'low' : 'high';

  const CFG = {
    fov: 40,
    // Referanstaki kamera turu. Kapalı bir CatmullRom eğrisine beslenir;
    // yaklaşık 6.3 birim yarıçapında, kum yüzeyinin ~1.5-6 birim üstünde.
    path: [
      [-7.799, 0.45, 9.103],
      [-5.05, 0.76, 7.05],
      [-4.313, 0.65, 3.701],
      [-5.74, 0.65, -0.22],
      [-3.051, 0.65, -5.277],
      [3.224, 0.65, -5.606],
      [6.46, 0.65, -0.22],
      [3.034, 0.65, 5.263],
      [-3.0, 0.65, 6.1],
    ],
    // Kum alanı: referansla aynı, 50.4 x 50.4 birim.
    field: 50.4,
    duneScale: 0.11,
    grains: QUALITY === 'high' ? 300000 : 135000,
    stars: QUALITY === 'high' ? 2600 : 900,
    // Ürün siluetleri: her kart kendi tanecik bulutunu taşır, aynı anda en
    // fazla bir-iki tanesi görünür olduğu için (uOpacity 0 iken de vertex
    // shader çalışır) toplam bütçe dune'un yanında küçük tutuluyor. El
    // kaldırıldığı için açılan bütçeyle biraz daha yoğun (daha "solid"
    // görünen) bir siluet için sayı artırıldı.
    silhouetteGrains: QUALITY === 'high' ? 8000 : 2600,
  };

  const COLORS = {
    a: css('--cine-grain-a', '#757575'),
    b: css('--cine-grain-b', '#ffffff'),
    glow: css('--cine-glow', '#f4f2ee'),
  };

  const FONT_STACK =
    '"IBM Plex Mono", ui-monospace, "Cascadia Code", "SF Mono", "Roboto Mono", "Courier New", monospace';

  // ---------------------------------------------------------------------
  // Fare kuyruğu - bağımsız 2B tuval katmanı (WebGL maliyeti yok).
  //
  // Müşterinin "önceki tasarımda vardı" dediği kuyruk aslında bu temanın
  // network-background.liquid snippet'inde hâlâ duruyor (yıldız kayması
  // gibi solan noktalar) - burada birebir aynı desen yeniden kuruldu.
  // ---------------------------------------------------------------------
  const hexToRgb = (hex, fallback) => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
    return m ? `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}` : fallback;
  };

  const trailCtx =
    trailCanvas && !window.matchMedia('(pointer: coarse)').matches ? trailCanvas.getContext('2d') : null;
  const trailParticles = [];
  const TRAIL_MAX = 90;
  let trailW = 0;
  let trailH = 0;
  let lastTrailX = null;
  let lastTrailY = null;

  function resizeTrail() {
    if (!trailCtx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvasMount.getBoundingClientRect();
    trailW = rect.width;
    trailH = rect.height;
    trailCanvas.width = trailW * dpr;
    trailCanvas.height = trailH * dpr;
    trailCanvas.style.width = `${trailW}px`;
    trailCanvas.style.height = `${trailH}px`;
    trailCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function drawTrail() {
    if (!trailCtx || !trailW || !trailH) return;
    trailCtx.clearRect(0, 0, trailW, trailH);
    const now = performance.now();
    const glowRGB = drawTrail.glowRGB || (drawTrail.glowRGB = hexToRgb(css('--cine-glow', '#f4f2ee'), '244, 242, 238'));
    for (let i = trailParticles.length - 1; i >= 0; i--) {
      const pt = trailParticles[i];
      const age = now - pt.born;
      if (age > pt.life) {
        trailParticles.splice(i, 1);
        continue;
      }
      const t = age / pt.life;
      const alpha = (1 - t) * 0.85;
      const r = pt.r * (1 - t * 0.55);
      trailCtx.beginPath();
      trailCtx.fillStyle = `rgba(${glowRGB}, ${alpha})`;
      trailCtx.shadowColor = `rgba(${glowRGB}, 0.9)`;
      trailCtx.shadowBlur = 9;
      trailCtx.arc(pt.x, pt.y, Math.max(0.1, r), 0, Math.PI * 2);
      trailCtx.fill();
    }
  }

  function pushTrail(clientX, clientY) {
    if (!trailCtx) return;
    const rect = canvasMount.getBoundingClientRect();
    const tx = clientX - rect.left;
    const ty = clientY - rect.top;
    if (lastTrailX !== null) {
      const dist = Math.hypot(tx - lastTrailX, ty - lastTrailY);
      const steps = Math.min(4, Math.max(1, Math.floor(dist / 14)));
      for (let s = 0; s < steps; s++) {
        if (trailParticles.length >= TRAIL_MAX) trailParticles.shift();
        const t = (s + 1) / steps;
        trailParticles.push({
          x: lastTrailX + (tx - lastTrailX) * t,
          y: lastTrailY + (ty - lastTrailY) * t,
          born: performance.now(),
          life: 500 + Math.random() * 250,
          r: 1.1 + Math.random() * 1.6,
        });
      }
    }
    lastTrailX = tx;
    lastTrailY = ty;
  }

  // ---------------------------------------------------------------------
  // CPU gürültüsü - kum tepelerinin yükseklik alanı
  // ---------------------------------------------------------------------
  function makeNoise2D(seed) {
    const hash = (x, y) => {
      let n = Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263) + Math.imul(seed, 1274126177);
      n = Math.imul(n ^ (n >>> 13), 1274126177);
      return ((n ^ (n >>> 16)) >>> 0) / 4294967295;
    };
    const fade = (t) => t * t * t * (t * (t * 6 - 15) + 10);
    return (x, y) => {
      const xi = Math.floor(x);
      const yi = Math.floor(y);
      const u = fade(x - xi);
      const v = fade(y - yi);
      const a = hash(xi, yi);
      const b = hash(xi + 1, yi);
      const c = hash(xi, yi + 1);
      const d = hash(xi + 1, yi + 1);
      return a * (1 - u) * (1 - v) + b * u * (1 - v) + c * (1 - u) * v + d * u * v;
    };
  }

  const noise2 = makeNoise2D(1337);

  /*
   * Ridged multifractal. Düz fbm ile kum "halı" gibi çıkıyordu: tepe
   * çizgisi yok, siluet yok. Katlama (1 - |2n-1|) keskin sırtlar üretir,
   * kare alma sırtları inceltir, bir önceki oktavla ağırlıklandırma da
   * detayı yalnızca sırtların üstünde toplar.
   */
  function ridged(x, y) {
    let amp = 0.5;
    let freq = 1;
    let sum = 0;
    let prev = 1;
    for (let i = 0; i < 5; i++) {
      let n = noise2(x * freq, y * freq);
      n = 1 - Math.abs(2 * n - 1);
      n = n * n;
      sum += n * amp * prev;
      prev = n;
      amp *= 0.5;
      freq *= 2.03;
    }
    return sum;
  }

  /*
   * ridged() toplamının aralığı katsayılara çok duyarlı - tahmin edilirse
   * arazi ya ekranın altında kalır ya kamerayı yutar. Bu yüzden aralık
   * kurulum sırasında örneklenip 0..1'e ölçekleniyor.
   */
  const ridgeRange = (() => {
    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < 4000; i++) {
      const v = ridged((Math.random() - 0.5) * CFG.field * CFG.duneScale, (Math.random() - 0.5) * CFG.field * CFG.duneScale);
      if (v < min) min = v;
      if (v > max) max = v;
    }
    return { min, span: Math.max(max - min, 1e-4) };
  })();

  const DUNE_BASE = -1.9;
  const DUNE_AMP = 4.2;

  /**
   * Kum yüzeyinin y değeri.
   *
   * `corridor` uçuş yoluna olan yatay uzaklıktan gelir: kameranın geçtiği
   * hat boyunca tepe yüksekliği bastırılır, kenarlara doğru serbest kalır.
   * Böylece kamera bir kanyonun içinden geçer - kum hiçbir zaman kamerayı
   * yutmaz ama iki yanda ve ileride tepeler ufku keser.
   */
  function duneSurface(x, z, corridor) {
    const n = clamp01((ridged(x * CFG.duneScale, z * CFG.duneScale) - ridgeRange.min) / ridgeRange.span);
    return DUNE_BASE + DUNE_AMP * corridor * Math.pow(n, 0.7);
  }

  // ---------------------------------------------------------------------
  // Shader kaynakları
  //
  // DİKKAT: GLSL bir JS template literal'i içinde yaşıyor - yorumlarda asla
  // backtick kullanma, string erken kapanır.
  // ---------------------------------------------------------------------
  const NOISE_GLSL = `
    vec3 hash3(vec3 p) {
      p = vec3(dot(p, vec3(127.1, 311.7, 74.7)),
               dot(p, vec3(269.5, 183.3, 246.1)),
               dot(p, vec3(113.5, 271.9, 124.6)));
      return fract(sin(p) * 43758.5453123) * 2.0 - 1.0;
    }
    float snoise3(vec3 p) {
      vec3 i = floor(p);
      vec3 f = fract(p);
      vec3 u = f * f * (3.0 - 2.0 * f);
      return mix(mix(mix(dot(hash3(i + vec3(0,0,0)), f - vec3(0,0,0)),
                         dot(hash3(i + vec3(1,0,0)), f - vec3(1,0,0)), u.x),
                     mix(dot(hash3(i + vec3(0,1,0)), f - vec3(0,1,0)),
                         dot(hash3(i + vec3(1,1,0)), f - vec3(1,1,0)), u.x), u.y),
                 mix(mix(dot(hash3(i + vec3(0,0,1)), f - vec3(0,0,1)),
                         dot(hash3(i + vec3(1,0,1)), f - vec3(1,0,1)), u.x),
                     mix(dot(hash3(i + vec3(0,1,1)), f - vec3(0,1,1)),
                         dot(hash3(i + vec3(1,1,1)), f - vec3(1,1,1)), u.x), u.y), u.z) * 1.6;
    }
    float hash12(vec2 p) {
      vec3 p3 = fract(vec3(p.xyx) * 0.1031);
      p3 += dot(p3, p3.yzx + 33.33);
      return fract((p3.x + p3.y) * p3.z);
    }
  `;

  /*
   * Kum taneciği vertex shader'ı. Referansın zincirini birebir izler:
   *   1. morph        : düz zeminden kum tepesi hacmine, gecikmeli dalga
   *   2. serbest sürüklenme + yükselen kıvılcımlar
   *   3. split mirror : taneciklerin yarısı y=0 etrafında aynalanıp yukarı
   *                     kaydırılır, koridor/tavan oluşur
   *   4. fare itmesi  : ekran uzayında yumuşak, girdaplı bir itiş
   *   5. kara delik   : dünya uzayında bir eksen etrafında spiral tünel
   */
  const GRAIN_VERTEX = `
    uniform vec2  uResolution;
    uniform float uSize;
    uniform float uTime;
    uniform float uProgress;
    uniform float uSplitProgress;
    uniform float uMirrorGap;
    uniform float uDrift;
    uniform float uFall;

    uniform vec2  uMouse;
    uniform float uMouseRadius;
    uniform float uMouseStrength;
    uniform float uMouseDepthFalloff;

    uniform float uBlackHoleProgress;
    uniform vec3  uBlackHoleCenter;
    uniform vec3  uBlackHoleAxis;
    uniform float uBlackHoleRadius;
    uniform float uBlackHoleSpin;
    uniform float uBlackHoleDepth;
    uniform float uBlackHoleTunnelRadius;
    uniform float uBlackHoleTunnelThickness;
    uniform float uBlackHoleOrbitTime;
    uniform float uBlackHoleSpiralPull;

    uniform vec2  uFieldMin;
    uniform vec2  uFieldMax;
    uniform float uEdgeFadeWidth;

    attribute vec3  aPositionTarget;
    attribute vec3  aNormal;
    attribute float aSize;
    attribute float aRnd;
    attribute float aRnd2;
    attribute float aHeight;
    // Zamandan bağımsız gürültüler CPU'da pişirilip buraya taşındı:
    // x = aynalama gecikmesi, y = kara delik gecikmesi, z = tünel kabuğu.
    attribute vec3  aNoise;

    varying vec3  vN;
    varying float vRnd;
    varying float vRnd2;
    varying float vCamFade;
    varying float vEdgeFade;
    varying float vNoise;

    void main() {
      vRnd = aRnd;
      vRnd2 = aRnd2;
      vNoise = aHeight;

      // --- 1. morph: düz zemin -> kum tepesi -------------------------------
      float heightMask  = smoothstep(-2.6, 2.1, aPositionTarget.y);
      float spatial     = smoothstep(-26.0, 26.0, position.x + position.z) * 0.34;
      float rnd         = floor(fract(aRnd * 13.37 + aRnd2) * 18.0) / 18.0;
      float delay       = spatial + (1.0 - heightMask) * 0.14 + rnd * 0.08;
      float endT        = min(delay + 0.34, 1.0);
      float mp          = smoothstep(delay, endT, uProgress);

      vec3 mixedPosition = mix(position, aPositionTarget, mp);
      mixedPosition.y -= sin(mp * 3.1415926) * 0.35;

      // --- 2. sürüklenme + yükselen kıvılcımlar -----------------------------
      float fallRnd  = fract(sin(dot(aPositionTarget.xz, vec2(12.9898, 78.233))) * 43758.5453);
      float fallMask = step(0.9955, fallRnd);
      float fallT    = fract(uTime * mix(0.6, 1.8, aRnd) * 0.004 + aRnd2);
      mixedPosition.y += fallT * mix(2.0, 6.0, aRnd2) * fallMask * uFall;

      float moveMask = step(0.5, fract(sin(dot(aPositionTarget.zx, vec2(91.173, 17.731))) * 43758.5453));
      float moveTime = uTime * 0.32 + aRnd * 62.8;
      // Buradaki sürüklenme genliği 0.02-0.09 birim; tek tek taneciklerin
      // kıpırdaması dışında bir şey ifade etmiyor. Eskiden üç ayrı 3B
      // simplex çağrısıyla yapılıyordu ve tek başına kare süresinin büyük
      // kısmını yiyordu - görsel olarak ayırt edilemeyen trigonometrik
      // salınım aynı işi görüyor.
      vec3 drift = vec3(
        sin(moveTime),
        cos(moveTime * 1.27 + aRnd2 * 31.4),
        sin(moveTime * 0.73 + aRnd2 * 12.6)
      );
      mixedPosition += drift * mix(0.02, 0.09, aRnd2) * moveMask * uDrift;

      // --- 3. split mirror --------------------------------------------------
      float splitMask = floor(fract(aRnd * 17.13 + aRnd2 * 31.77) * 2.0);
      vec3 lowerPosition = mixedPosition;
      lowerPosition.y = -mixedPosition.y + uMirrorGap;

      float splitNoise = aNoise.x;
      float splitDelay =
        smoothstep(-8.0, 8.0, mixedPosition.x) * 0.22 +
        splitNoise * 0.18 +
        (floor(fract(aRnd * 23.17 + aRnd2 * 41.93) * 12.0) / 12.0) * 0.14;
      float splitT = smoothstep(splitDelay, min(splitDelay + 0.34, 1.0), uSplitProgress) * splitMask;
      lowerPosition.y += sin(splitT * 3.1415926) * 0.35;
      mixedPosition = mix(mixedPosition, lowerPosition, splitT);

      // --- 4. fare itmesi ---------------------------------------------------
      vec4 modelPre = modelMatrix * vec4(mixedPosition, 1.0);
      vec4 viewPre  = viewMatrix * modelPre;
      vec4 clipPre  = projectionMatrix * viewPre;
      vec2 ndc = clipPre.xy / max(clipPre.w, 0.0001);

      // Fare alanının düzgün bir daire olmaması için tanecik başına sabit
      // bir kaçıklık yeterli; bunun için ayrıca gürültü örneklemeye gerek yok.
      vec2 diff = ndc - uMouse;
      vec2 warped = diff + vec2(aRnd - 0.5, aRnd2 - 0.5) * 0.12;

      float localRadius = uMouseRadius * (0.88 + 0.34 * aRnd2);
      float distMouse = length(warped);
      float influence = pow(1.0 - smoothstep(0.0, localRadius, distMouse), 1.35);
      influence *= 1.0 - (1.0 - smoothstep(0.0, localRadius * 0.35, distMouse)) * 0.45;

      float depthAtt = 1.0 / (1.0 + max(-viewPre.z, 0.0) * uMouseDepthFalloff * 0.15);
      vec3 modelNormal = normalize((modelMatrix * vec4(aNormal, 0.0)).xyz);

      vec2 dir2 = distMouse > 0.0001 ? normalize(warped) : vec2(0.0);
      vec2 tangent = vec2(-dir2.y, dir2.x);
      vec2 finalDir = normalize(dir2 + tangent * ((aRnd - 0.5) * 0.6));

      vec3 camRight = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
      vec3 camUp    = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);
      vec3 push = normalize(camRight * finalDir.x + camUp * finalDir.y + modelNormal * 0.35);

      mixedPosition += push * (uMouseStrength * influence * depthAtt * mix(0.85, 1.15, aRnd));
      mixedPosition += modelNormal * (uMouseStrength * 0.25 * influence * depthAtt);

      // --- 5. kara delik tüneli --------------------------------------------
      vec4 modelPosition = modelMatrix * vec4(mixedPosition, 1.0);

      float bhNoise = aNoise.y;
      float bhDelay = bhNoise * 0.20 + (floor(fract(aRnd * 29.71 + aRnd2 * 11.43) * 14.0) / 14.0) * 0.16;
      float bhT = smoothstep(bhDelay, min(bhDelay + 0.48, 1.0), uBlackHoleProgress);
      bhT = bhT * bhT * (3.0 - 2.0 * bhT);

      vec3 axis = normalize(uBlackHoleAxis);
      vec3 ref  = abs(axis.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
      vec3 sideA = normalize(cross(ref, axis));
      vec3 sideB = normalize(cross(axis, sideA));

      vec3 rel = modelPosition.xyz - uBlackHoleCenter;
      float reach = 1.0 - smoothstep(uBlackHoleRadius, uBlackHoleRadius + 6.0, length(rel));
      float amount = bhT * reach;

      float axial = dot(rel, axis);
      vec3 radial = rel - axis * axial;
      float radialDist = length(radial);
      vec3 radialDir = radialDist > 0.0001
        ? radial / radialDist
        : normalize(sideA * cos(aRnd * 6.2831853) + sideB * sin(aRnd * 6.2831853));

      float angle = atan(dot(radialDir, sideB), dot(radialDir, sideA));
      float radiusN = clamp(radialDist / max(uBlackHoleRadius, 0.001), 0.0, 1.0);

      float shellRadius = uBlackHoleTunnelRadius
        + aNoise.z * uBlackHoleTunnelThickness
        + (aRnd - 0.5) * uBlackHoleTunnelThickness;

      float lane = floor((aRnd * 0.82 + aRnd2 * 0.18 + bhNoise * 0.035) * 18.0) / 18.0;

      float vortexT = smoothstep(0.02, 0.54, uBlackHoleProgress) * reach;
      vortexT = vortexT * vortexT * (3.0 - 2.0 * vortexT);
      float formT = max(smoothstep(0.34, 0.70, uBlackHoleProgress) * reach, pow(amount, 1.15) * 0.82);

      float inward = clamp(uBlackHoleSpiralPull * formT * mix(0.72, 1.18, aRnd2), 0.0, 0.82);
      float targetRadius = max(0.1, mix(radialDist, shellRadius, formT) * (1.0 - inward));

      float spiralAngle = angle
        + uBlackHoleSpin * 6.2831853 * vortexT * (1.12 - radiusN * 0.32)
        + (aRnd2 - 0.5) * 0.65 * vortexT
        + uBlackHoleOrbitTime * 2.4 * formT;

      float depthOffset = uBlackHoleDepth * lane * formT
        + uBlackHoleDepth * 0.28 * vortexT
        + uBlackHoleDepth * 0.35 * pow(amount, 1.15)
        + (aRnd - 0.5) * uBlackHoleDepth * 0.06 * formT;

      vec3 ringDir = sideA * cos(spiralAngle) + sideB * sin(spiralAngle);
      vec3 tunnelPosition = uBlackHoleCenter + axis * (axial + depthOffset) + ringDir * targetRadius;

      modelPosition.xyz = mix(modelPosition.xyz, tunnelPosition, step(0.0001, uBlackHoleProgress));

      // --- son dönüşüm ------------------------------------------------------
      vec4 viewPosition = viewMatrix * modelPosition;
      vN = normalize(normalMatrix * aNormal);

      float dist = -viewPosition.z;
      if (dist <= 0.0001) {
        gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
        gl_PointSize = 0.0;
        vCamFade = 0.0;
        return;
      }

      vCamFade = smoothstep(0.25, 1.0, dist) * mix(0.75, 1.0, vRnd);
      gl_Position = projectionMatrix * viewPosition;

      float px = uSize * mix(0.35, 1.35, aSize) * mix(0.75, 1.25, vRnd);
      px *= 1.0 + 0.06 * sin(uTime * 0.8 + vRnd2 * 10.0);
      px *= 1.0 + amount * 0.45;
      gl_PointSize = clamp(px * (uResolution.y / dist), 1.0, 46.0);

      vec2 p2 = aPositionTarget.xz;
      float edgeDist = min(
        min(p2.x - uFieldMin.x, uFieldMax.x - p2.x),
        min(p2.y - uFieldMin.y, uFieldMax.y - p2.y)
      );
      vEdgeFade = smoothstep(0.0, uEdgeFadeWidth, edgeDist);
    }
  `;

  /*
   * Fragment shader: her tanecik bir yumuşak disk. Referanstaki gibi
   * core/halo/edge maskeleri ayrı ayrı kontrol edilir, üstüne per-particle
   * UV bükme (aynı yuvarlak damgayı tekrarlamamak için) ve sahte ışık
   * eklenir. Sonuç uGlow ile çarpılıp additive olarak yazılır.
   */
  const GRAIN_FRAGMENT = `
    uniform vec3  uColorA;
    uniform vec3  uColorB;
    uniform float uTime;
    uniform float uOpacity;
    uniform float uAlpha;
    uniform float uGlow;
    uniform float uCore;
    uniform float uCoreSoft;
    uniform float uHalo;
    uniform float uHaloSoft;
    uniform float uHaloStrength;
    uniform float uEdge;
    uniform float uEdgeSoft;
    uniform vec3  uLightDir;
    uniform float uBase;
    uniform float uDiffuse;
    uniform float uRim;
    uniform float uSpec;
    uniform float uSpecPow;
    uniform float uLightWrap;

    varying vec3  vN;
    varying float vRnd;
    varying float vRnd2;
    varying float vCamFade;
    varying float vEdgeFade;
    varying float vNoise;

    float hash12(vec2 p) {
      vec3 p3 = fract(vec3(p.xyx) * 0.1031);
      p3 += dot(p3, p3.yzx + 33.33);
      return fract((p3.x + p3.y) * p3.z);
    }
    mat2 rot(float a) { float s = sin(a), c = cos(a); return mat2(c, -s, s, c); }

    void main() {
      // Sprite'ın her tanecikte aynı yuvarlak damga gibi görünmemesi için
      // döndürülüp bozuluyor. Bozma eskiden iki hash çağrısı kullanıyordu;
      // tek çağrı ile türetilen iki bileşen görsel olarak aynı sonucu
      // veriyor ve fragment başına bir hash tasarruf ediyor.
      vec2 pp = gl_PointCoord - 0.5;
      pp = rot(vRnd * 6.2831853) * pp;
      float w = hash12(pp * 9.0 + vRnd + uTime * 12.0);
      pp += (vec2(w, fract(w * 7.13)) - 0.5) * mix(0.12, 0.35, vRnd2);

      float d = length(pp);

      float core = smoothstep(uCore, max(uCore - uCoreSoft, 0.0001), d);
      float halo = smoothstep(uHalo, max(uHalo - uHaloSoft, 0.0001), d) * uHaloStrength;
      float edge = smoothstep(uEdge, max(uEdge - uEdgeSoft, 0.0001), d);

      float outA = clamp((core + halo) * edge, 0.0, 1.0);
      outA *= uOpacity * uAlpha * vCamFade * vEdgeFade;
      if (outA < 0.01) discard;

      // Tanecikli görünüm: piksellerin bir kısmını rastgele düşür.
      if (edge < hash12(gl_FragCoord.xy + vRnd * 1000.0) * 0.85) discard;

      vec3 N = normalize(vN);
      vec3 L = normalize(uLightDir);
      vec3 V = vec3(0.0, 0.0, 1.0);

      float diff = clamp((dot(N, L) + uLightWrap) / (1.0 + uLightWrap), 0.0, 1.0);
      float rim  = pow(1.0 - clamp(dot(N, V), 0.0, 1.0), 2.0);
      float spec = pow(max(dot(N, normalize(L + V)), 0.0), uSpecPow) * uSpec;
      // Parıltıyı sadece bazı tanecikler alsın - tanecik başına sabit bir
      // maske, fragment başına hash'ten çok daha ucuz.
      spec *= step(0.72, vRnd2);

      vec3 col = vec3(uBase) + diff * uDiffuse + rim * uRim + spec;
      col *= 0.05;
      col = max(col, vec3(0.02));

      vec3 tint = mix(uColorA, uColorB, vNoise);
      gl_FragColor = vec4(col * tint * uGlow, outA);
    }
  `;

  // Yıldızlar: küçük, sabit, kırpışan noktalar.
  const STAR_VERTEX = `
    uniform vec2  uResolution;
    uniform float uSize;
    uniform float uTime;
    uniform float uProgress;
    attribute float aRnd;
    attribute float aRnd2;
    varying float vTwinkle;
    varying float vRnd;
    void main() {
      vRnd = aRnd;
      vec3 p = position;
      p.y = mix(p.y - 22.0, p.y, smoothstep(0.0, 1.0, uProgress));
      vec4 viewPosition = viewMatrix * modelMatrix * vec4(p, 1.0);
      float dist = max(-viewPosition.z, 0.0001);
      gl_Position = projectionMatrix * viewPosition;
      vTwinkle = 0.45 + 0.55 * pow(abs(sin(uTime * mix(0.3, 1.4, aRnd2) + aRnd * 22.0)), 3.0);
      gl_PointSize = clamp(uSize * mix(0.4, 1.6, aRnd) * (uResolution.y / dist), 1.0, 14.0);
    }
  `;

  const STAR_FRAGMENT = `
    uniform float uOpacity;
    uniform vec3  uColor;
    varying float vTwinkle;
    varying float vRnd;
    void main() {
      float d = length(gl_PointCoord - 0.5);
      float core = smoothstep(0.16, 0.0, d);
      float halo = smoothstep(0.5, 0.05, d) * 0.55;
      float a = (core + halo) * uOpacity * vTwinkle;
      if (a < 0.01) discard;
      gl_FragColor = vec4(uColor * (0.35 + vRnd * 0.65), a);
    }
  `;

  // Sis düzlemleri: kameranın önünde duran iki büyük quad.
  const FOG_VERTEX = `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `;

  /*
   * Sis.
   *
   * İlk sürüm fbm'i doğrudan fragment shader'da hesaplıyordu: iki tam ekran
   * düzlem x piksel başına yedi fbm x beş oktav x dört hash = piksel başına
   * ~280 hash. Ölçümde tek başına kare süresinin %80'ini (44.6 ms'nin 35
   * ms'sini) yiyordu. Referans da zaten hazır bir bulut dokusu (cloud.png)
   * kullanıyordu; burada aynı fbm kurulum sırasında 256x256 döşenebilir bir
   * dokuya pişiriliyor ve shader dört doku okumasına iniyor.
   */
  const FOG_FRAGMENT = `
    uniform float uTime;
    uniform float uOpacity;
    uniform float uDensity;
    uniform float uSpeed;
    uniform float uNoiseScale;
    uniform vec3  uColorDark;
    uniform vec3  uColorLight;
    uniform vec2  uMouse;
    uniform float uMouseRadius;
    uniform float uMouseStrength;
    uniform sampler2D uNoiseTex;
    varying vec2 vUv;

    void main() {
      if (uOpacity < 0.0006) discard;

      vec2 uv = vUv;
      float t = uTime * uSpeed;

      vec2 diff = uv - (uMouse * 0.5 + 0.5);
      float dist = length(diff);
      float influence = pow(1.0 - smoothstep(0.0, uMouseRadius, dist), 1.35);
      vec2 dir = dist > 0.0001 ? diff / dist : vec2(0.0);
      vec2 fogUv = uv
        + dir * (uMouseStrength * 0.35 * influence)
        + vec2(-dir.y, dir.x) * (uMouseStrength * 0.55 * influence);

      vec2 s = fogUv * uNoiseScale;

      // Alan bükme: dumanın akıcı, kıvrımlı görünmesini sağlayan şey.
      vec2 w = vec2(
        texture2D(uNoiseTex, s * 0.35 + vec2(t * 0.03, -t * 0.02)).b,
        texture2D(uNoiseTex, s * 0.4 + vec2(-t * 0.02, t * 0.025)).b
      );
      w = (w - 0.5) * 0.45;

      float n1 = texture2D(uNoiseTex, s * 0.55 + w + vec2(t * 0.05, -t * 0.03)).r;
      float n2 = texture2D(uNoiseTex, s * 1.15 + w * 1.6 + vec2(-t * 0.04, t * 0.02)).g;
      float n = smoothstep(0.34, 0.9, n1 * 0.62 + n2 * 0.48);

      float shape = smoothstep(0.0, 0.18, fogUv.y) * (1.0 - smoothstep(0.68, 1.0, fogUv.y))
                  * smoothstep(0.0, 0.08, fogUv.x) * (1.0 - smoothstep(0.92, 1.0, fogUv.x));

      float alpha = clamp(shape * mix(0.68, 1.0, n) * uDensity * uOpacity, 0.0, 1.0);
      gl_FragColor = vec4(mix(uColorDark, uColorLight, clamp(n + influence * 0.05, 0.0, 1.0)), alpha);
    }
  `;

  /*
   * Dünya uzayındaki yazılar. Referans MSDF atlası kullanıyordu; burada
   * metni bir canvas'a çizip tek düzleme yapıştırıyoruz ve açığa çıkarmayı
   * (reveal) fragment shader'da yapıyoruz:
   *   uReveal 0 -> 1 : soldan sağa, gürültüyle taneli biçimde belirir
   *   uReveal 1 -> 2 : ters yönde dağılıp kaybolur
   */
  const TEXT_VERTEX = `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `;

  const TEXT_FRAGMENT = `
    uniform sampler2D uMap;
    uniform vec3  uColor;
    uniform float uReveal;
    uniform float uOpacity;
    uniform float uTime;
    uniform float uGrain;
    varying vec2 vUv;

    float hash12(vec2 p) {
      vec3 p3 = fract(vec3(p.xyx) * 0.1031);
      p3 += dot(p3, p3.yzx + 33.33);
      return fract((p3.x + p3.y) * p3.z);
    }

    void main() {
      float mask = texture2D(uMap, vUv).a;
      if (mask < 0.004) discard;

      vec2 cell = floor(vUv * vec2(260.0, 90.0));
      float n1 = hash12(cell);
      float n2 = hash12(cell + 41.7);

      float phase = clamp(uReveal, 0.0, 1.0);
      float outp  = clamp(uReveal - 1.0, 0.0, 1.0);

      float d1 = vUv.x * 0.45 + n1 * 0.55;
      float inMask = smoothstep(d1 * 0.72, d1 * 0.72 + 0.3, phase);

      float d2 = (1.0 - vUv.x) * 0.45 + n2 * 0.55;
      float outMask = 1.0 - smoothstep(d2 * 0.72, d2 * 0.72 + 0.3, outp);

      float transition = max(phase * (1.0 - phase), outp * (1.0 - outp)) * 4.0;
      float grain = mix(1.0, 0.35 + 0.65 * hash12(cell + floor(uTime * 12.0)), transition * uGrain);

      float a = mask * inMask * outMask * uOpacity * grain;
      if (a < 0.004) discard;
      gl_FragColor = vec4(uColor, a);
    }
  `;

  // Post-process
  const QUAD_VERTEX = `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = vec4(position.xy, 0.0, 1.0);
    }
  `;

  const BRIGHT_FRAGMENT = `
    uniform sampler2D uMap;
    uniform float uThreshold;
    uniform float uSmoothing;
    varying vec2 vUv;
    void main() {
      vec3 c = texture2D(uMap, vUv).rgb;
      float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
      gl_FragColor = vec4(c * smoothstep(uThreshold, uThreshold + uSmoothing, l), 1.0);
    }
  `;

  const BLUR_FRAGMENT = `
    uniform sampler2D uMap;
    uniform vec2 uDirection;
    varying vec2 vUv;
    void main() {
      vec3 sum = texture2D(uMap, vUv).rgb * 0.227027;
      sum += (texture2D(uMap, vUv + uDirection * 1.3846).rgb
            + texture2D(uMap, vUv - uDirection * 1.3846).rgb) * 0.316216;
      sum += (texture2D(uMap, vUv + uDirection * 3.2307).rgb
            + texture2D(uMap, vUv - uDirection * 3.2307).rgb) * 0.070270;
      gl_FragColor = vec4(sum, 1.0);
    }
  `;

  const COMPOSITE_FRAGMENT = `
    uniform sampler2D uScene;
    uniform sampler2D uBloom;
    uniform float uIntensity;
    uniform float uExposure;
    uniform float uVignette;
    varying vec2 vUv;
    void main() {
      vec3 base = texture2D(uScene, vUv).rgb;
      vec3 bloom = texture2D(uBloom, vUv).rgb;
      vec3 col = base + bloom * uIntensity;

      col *= uExposure;
      // Reinhard - referanstaki tone mapping ile aynı aile
      col = col / (1.0 + col);

      vec2 v = vUv - 0.5;
      col *= 1.0 - dot(v, v) * uVignette;

      gl_FragColor = vec4(col, 1.0);
    }
  `;

  // ---------------------------------------------------------------------
  // Metin dokusu
  // ---------------------------------------------------------------------
  function textTexture(THREE, text, opts) {
    const o = Object.assign({ size: 108, tracking: 0.24, weight: 500, pad: 0.35 }, opts || {});
    const chars = [...String(text)];
    const measure = document.createElement('canvas').getContext('2d');
    const font = `${o.weight} ${o.size}px ${FONT_STACK}`;
    measure.font = font;

    const gap = o.size * o.tracking;
    let width = 0;
    const advances = chars.map((ch) => {
      const w = measure.measureText(ch).width;
      width += w + gap;
      return w;
    });
    width = Math.max(width - gap, 1);

    const padX = o.size * o.pad;
    const padY = o.size * o.pad;
    const cw = Math.ceil(width + padX * 2);
    const chh = Math.ceil(o.size * 1.45 + padY * 2);

    const canvas = document.createElement('canvas');
    canvas.width = Math.min(cw, 4096);
    canvas.height = Math.min(chh, 1024);
    const ctx = canvas.getContext('2d');
    ctx.font = font;
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffffff';

    let x = padX;
    const y = canvas.height / 2;
    for (let i = 0; i < chars.length; i++) {
      ctx.fillText(chars[i], x, y);
      x += advances[i] + gap;
    }

    const tex = new THREE.CanvasTexture(canvas);
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    tex.anisotropy = 4;
    return { texture: tex, aspect: canvas.width / canvas.height };
  }

  // ---------------------------------------------------------------------
  // Hacimsel örnekleme - kapsül/elipsoit birleşiminden nokta bulutu üretir.
  //
  // Ürün siluetleri (aşağıda ARCHETYPES sözlüğü) bu yardımcıyla
  // örnekleniyor: müşterinin referans gösterdiği "iç içe geçmiş küre
  // kümesi" görünümü tam olarak bu tekniğin çıktısı. Sınır kutusu parça
  // listesinden otomatik hesaplanıyor, bu yüzden her yeni arketip için
  // elle kutu tanımlamaya gerek yok.
  const capsule = (a, b, ra, rb) => ({ kind: 'capsule', a, b, ra, rb });
  const ellipsoid = (c, r) => ({ kind: 'ellipsoid', c, r });

  function boundsOf(parts, pad) {
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    const grow = (c, r) => {
      for (let i = 0; i < 3; i++) {
        min[i] = Math.min(min[i], c[i] - r);
        max[i] = Math.max(max[i], c[i] + r);
      }
    };
    parts.forEach((p) => {
      if (p.kind === 'capsule') {
        const r = Math.max(p.ra, p.rb);
        grow(p.a, r);
        grow(p.b, r);
      } else {
        grow(p.c, Math.max(p.r[0], p.r[1], p.r[2]));
      }
    });
    return {
      x: [min[0] - pad, max[0] + pad],
      y: [min[1] - pad, max[1] + pad],
      z: [min[2] - pad, max[2] + pad],
    };
  }

  /*
   * Yüzey (kabuk) örneklemesi.
   *
   * İlk deneme parçaların İÇ HACMİNİ düzgün dolduruyordu - referans
   * fotoğraftaki gibi net, "kolayca okunan" bir siluet vermedi, çünkü
   * bütçenin büyük kısmı örtüşen elipsoitlerin GÖRÜNMEYEN iç kesişimine
   * gidiyordu. Aynı hata daha önce elde de vardı ("ele benzemiyor"
   * şikayeti). Çözüm: her parçanın YÜZEYinden örnekle, başka bir parçanın
   * içinde kalan (yani dıştan görünmeyen) noktaları ele - kalan nokta
   * bulutu tam olarak birleşimin dış kabuğu, aynı parçacık bütçesiyle çok
   * daha net bir siluet veriyor.
   */
  function sampleShell(parts, count) {
    const insideCapsule = (x, y, z, p) => {
      const abx = p.b[0] - p.a[0];
      const aby = p.b[1] - p.a[1];
      const abz = p.b[2] - p.a[2];
      const px = x - p.a[0];
      const py = y - p.a[1];
      const pz = z - p.a[2];
      const t = clamp((px * abx + py * aby + pz * abz) / (abx * abx + aby * aby + abz * abz), 0, 1);
      const dx = px - abx * t;
      const dy = py - aby * t;
      const dz = pz - abz * t;
      const r = lerp(p.ra, p.rb, t) * 0.985;
      return dx * dx + dy * dy + dz * dz <= r * r;
    };
    const insideEllipsoid = (x, y, z, p) => {
      const dx = (x - p.c[0]) / (p.r[0] * 0.985);
      const dy = (y - p.c[1]) / (p.r[1] * 0.985);
      const dz = (z - p.c[2]) / (p.r[2] * 0.985);
      return dx * dx + dy * dy + dz * dz <= 1;
    };
    const insideOther = (x, y, z, exceptIdx) =>
      parts.some(
        (p, i) => i !== exceptIdx && (p.kind === 'capsule' ? insideCapsule(x, y, z, p) : insideEllipsoid(x, y, z, p))
      );

    // Parça başına ağırlık ~ yüzey alanı (ortalama yarıçapın karesi), her
    // parça bütçeden hacmiyle orantılı pay alsın diye.
    const weights = parts.map((p) => {
      const r = p.kind === 'capsule' ? (p.ra + p.rb) / 2 : (p.r[0] + p.r[1] + p.r[2]) / 3;
      return Math.max(r * r, 1e-4);
    });
    const totalWeight = weights.reduce((a, b) => a + b, 0);

    const arr = new Float32Array(count * 3);
    let accepted = 0;
    let iterations = 0;
    const maxIterations = count * 400;
    while (accepted < count && iterations < maxIterations) {
      iterations++;
      let w = Math.random() * totalWeight;
      let idx = 0;
      while (idx < weights.length - 1 && w > weights[idx]) {
        w -= weights[idx];
        idx++;
      }
      const p = parts[idx];

      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const sx = Math.sin(phi) * Math.cos(theta);
      const sy = Math.cos(phi);
      const sz = Math.sin(phi) * Math.sin(theta);

      let x, y, z;
      if (p.kind === 'capsule') {
        const t = Math.random();
        const cx = lerp(p.a[0], p.b[0], t);
        const cy = lerp(p.a[1], p.b[1], t);
        const cz = lerp(p.a[2], p.b[2], t);
        const r = lerp(p.ra, p.rb, t);
        x = cx + sx * r;
        y = cy + sy * r;
        z = cz + sz * r;
      } else {
        x = p.c[0] + sx * p.r[0];
        y = p.c[1] + sy * p.r[1];
        z = p.c[2] + sz * p.r[2];
      }

      if (insideOther(x, y, z, idx)) continue;
      arr[accepted * 3] = x;
      arr[accepted * 3 + 1] = y;
      arr[accepted * 3 + 2] = z;
      accepted++;
    }
    for (let s = accepted; s < count; s++) {
      const src = Math.floor(Math.random() * Math.max(accepted, 1)) * 3;
      arr[s * 3] = arr[src] || 0;
      arr[s * 3 + 1] = arr[src + 1] || 0;
      arr[s * 3 + 2] = arr[src + 2] || 0;
    }
    return { positions: arr, bounds: boundsOf(parts, 0.02) };
  }

  /*
   * Ürün silueti arketipleri.
   *
   * İlk sürümde yalnızca iki jenerik form vardı (koltuk/sehpa), bu yüzden
   * mağazadaki 16 ürün ekranda hep aynı iki bulut gibi görünüyordu.
   * Mobilya sayfasındaki gerçek 16 ürün fotoğrafı tek tek incelenip
   * (assets/urun-*.jpg) her birine kendi siluetini veren ayrı bir arketip
   * yazıldı - artık her ürün kendi gerçek formunun (kum saati sehpa, kafes
   * koltuk, zигзаг masa, halka tabure, bükülmüş şerit sezlong, istiflenmiş
   * saksı...) tanecik versiyonunu alıyor. `ARCHETYPES` sözlüğü doğrudan
   * bloğun `fallback_image_key` değeriyle eşleşiyor (bkz. archetypeFor).
   */

  // LOON - referans fotoğrafın ta kendisi: örtüşen küre kümesi koltuk.
  const SPHERE_CLUSTER_ARMCHAIR = [
    ellipsoid([0, 0.2, 0], [0.44, 0.46, 0.42]),
    ellipsoid([0.44, 0.36, -0.1], [0.34, 0.36, 0.34]),
    ellipsoid([-0.46, 0.02, 0.16], [0.2, 0.2, 0.2]),
    ellipsoid([-0.58, 0.52, 0.14], [0.1, 0.1, 0.1]),
    ellipsoid([0.02, -0.28, 0.3], [0.38, 0.28, 0.38]),
    ellipsoid([0.68, 0.12, -0.08], [0.28, 0.3, 0.28]),
    ellipsoid([0.8, -0.18, -0.22], [0.17, 0.17, 0.17]),
    ellipsoid([0.02, -0.7, 0.0], [0.48, 0.18, 0.44]),
  ];

  // Jenerik sehpa - dört ince bacaklı düz tepeli masa (spesifik eşleşme
  // bulunamayan "Sehpa" kategorisindeki bloklar için yedek).
  const FOUR_LEG_TABLE = [
    ellipsoid([0, 0.32, 0], [0.62, 0.1, 0.4]),
    ellipsoid([-0.46, -0.18, -0.26], [0.09, 0.42, 0.09]),
    ellipsoid([0.46, -0.18, -0.26], [0.09, 0.42, 0.09]),
    ellipsoid([-0.46, -0.18, 0.26], [0.09, 0.42, 0.09]),
    ellipsoid([0.46, -0.18, 0.26], [0.09, 0.42, 0.09]),
    ellipsoid([0, 0.3, 0], [0.2, 0.06, 0.2]),
  ];

  // NOI - alçak, geniş, tek parça köşeli blok sehpa (kırmızı çerçeveli).
  const LOW_BLOCK_TABLE = [
    ellipsoid([0, 0.02, 0], [0.85, 0.22, 0.42]),
    ellipsoid([-0.3, 0.1, 0.05], [0.22, 0.16, 0.3]),
  ];

  // EVE - kum saati / trompet ayaklı yuvarlak sehpa: geniş üst, ince bel,
  // geniş taban.
  const TRUMPET_TABLE = [
    ellipsoid([0, 0.44, 0], [0.6, 0.07, 0.6]),
    capsule([0, 0.38, 0], [0, 0.04, 0], 0.42, 0.12),
    capsule([0, 0.04, 0], [0, -0.34, 0], 0.12, 0.5),
    ellipsoid([0, -0.4, 0], [0.52, 0.06, 0.52]),
  ];

  // AETHER - kafes/voronoi baskı koltuk: yumuşak gövde + dört ince
  // uzanan bacak (yapının inceliğini hissettirmek için).
  const LATTICE_ARMCHAIR = [
    ellipsoid([0.05, 0.32, -0.05], [0.42, 0.4, 0.4]),
    ellipsoid([0.1, -0.05, 0.15], [0.4, 0.28, 0.42]),
    capsule([-0.32, -0.15, -0.28], [-0.42, -0.78, -0.34], 0.1, 0.05),
    capsule([0.4, -0.15, -0.28], [0.5, -0.78, -0.34], 0.1, 0.05),
    capsule([-0.32, -0.15, 0.32], [-0.4, -0.78, 0.42], 0.1, 0.05),
    capsule([0.4, -0.15, 0.32], [0.48, -0.78, 0.42], 0.1, 0.05),
  ];

  // AKASHA / NATS - yumuşak, alçak, geniş kaplama bench; arkada hafif
  // yükselen bir kıvrım şeridi.
  const SOFT_BENCH = [
    ellipsoid([0, -0.06, 0], [0.75, 0.32, 0.4]),
    ellipsoid([0, 0.28, -0.12], [0.68, 0.14, 0.2]),
  ];

  // BOUCHE - sivri burunlu, tek kabuk yumuşak koltuk (geminin pruvası
  // gibi bir köşeye toplanıyor).
  const SOFT_WEDGE_CHAIR = [
    ellipsoid([0.18, 0.04, 0], [0.55, 0.42, 0.5]),
    ellipsoid([-0.42, -0.08, 0.02], [0.3, 0.28, 0.26]),
  ];

  // ARAXYS - alçak, iki ucu incelen sörf tahtası / mercek biçimli bench.
  const LOZENGE_BENCH = [
    ellipsoid([0, 0, 0], [0.78, 0.16, 0.42]),
    ellipsoid([0.86, 0, 0], [0.26, 0.1, 0.26]),
    ellipsoid([-0.86, 0, 0], [0.26, 0.1, 0.26]),
  ];

  // LIMBUS - iki ince düz plaka, çapraz bir kolla birbirine bağlı
  // (zigzag origami sehpa).
  const ZIGZAG_SLAB = [
    ellipsoid([-0.42, 0.3, 0], [0.55, 0.05, 0.4]),
    ellipsoid([0.42, -0.3, 0], [0.55, 0.05, 0.4]),
    capsule([-0.14, 0.22, 0], [0.14, -0.22, 0], 0.1, 0.1),
  ];

  // LUME - yuvarlak köşeli, düz yatan dikdörtgen halka tabure.
  const LOOP_FRAME = [
    capsule([-0.5, 0, -0.32], [0.5, 0, -0.32], 0.14, 0.14),
    capsule([-0.5, 0, 0.32], [0.5, 0, 0.32], 0.14, 0.14),
    capsule([-0.5, 0, -0.32], [-0.5, 0, 0.32], 0.14, 0.14),
    capsule([0.5, 0, -0.32], [0.5, 0, 0.32], 0.14, 0.14),
  ];

  // LUSIO - ince, düz, katlanmış şerit sezlong (Z biçiminde iki kırım).
  const ANGULAR_FOLD_CHAIR = [
    capsule([-0.62, -0.32, 0], [0.04, -0.34, 0], 0.1, 0.1),
    capsule([0.04, -0.34, 0], [0.16, 0.04, 0], 0.1, 0.09),
    capsule([0.16, 0.04, 0], [0.68, 0.56, 0], 0.09, 0.08),
  ];

  // YASUKE - bükülmüş, akışkan bir şerit halinde uzanan sezlong; alçak-
  // yüksek-alçak bir S eğrisi boyunca dizilen elipsoit zinciri.
  const TWISTED_RIBBON = [
    ellipsoid([-0.58, -0.16, 0], [0.28, 0.14, 0.3]),
    ellipsoid([-0.22, 0.06, 0.06], [0.3, 0.16, 0.3]),
    ellipsoid([0.16, 0.26, -0.05], [0.3, 0.18, 0.28]),
    ellipsoid([0.52, 0.1, 0.06], [0.28, 0.16, 0.3]),
    ellipsoid([0.78, -0.2, -0.05], [0.24, 0.13, 0.26]),
  ];

  // DEZAKSE - istiflenmiş çakıl/halka biçimli saksı; üst üste binen
  // yassı elipsoit halkalar.
  const STACKED_RINGS = [
    ellipsoid([0, -0.5, 0], [0.5, 0.12, 0.5]),
    ellipsoid([0, -0.26, 0], [0.44, 0.12, 0.44]),
    ellipsoid([0, -0.02, 0], [0.47, 0.12, 0.47]),
    ellipsoid([0, 0.22, 0], [0.4, 0.12, 0.4]),
    ellipsoid([0, 0.46, 0], [0.32, 0.11, 0.32]),
  ];

  // PINE - uzun, hafif fıçı biçimli örgü saksı.
  const TALL_LATTICE_VASE = [
    ellipsoid([0, -0.5, 0], [0.32, 0.18, 0.32]),
    ellipsoid([0, -0.2, 0], [0.4, 0.2, 0.4]),
    ellipsoid([0, 0.15, 0], [0.36, 0.2, 0.36]),
    ellipsoid([0, 0.5, 0], [0.26, 0.2, 0.26]),
  ];

  // WAVE - ince, dalgalı (S kıvrımlı) duvar rafı.
  const WAVY_SHELF = [
    ellipsoid([-0.5, 0.08, 0], [0.35, 0.05, 0.18]),
    ellipsoid([0, -0.08, 0], [0.35, 0.05, 0.18]),
    ellipsoid([0.5, 0.08, 0], [0.35, 0.05, 0.18]),
  ];

  // BOTENOBO - iki katmanlı ince plaka + bakır silindir ayak.
  const LAYERED_PEDESTAL = [
    ellipsoid([0.3, 0.3, 0], [0.5, 0.05, 0.4]),
    ellipsoid([-0.3, -0.3, 0], [0.5, 0.05, 0.4]),
    capsule([0, 0.25, 0], [0, -0.65, 0], 0.16, 0.16),
  ];

  // fallback_image_key -> arketip. Tema editöründeki ürün seçici listesiyle
  // birebir eşleşiyor (bkz. sections/cinematic-intro.liquid şeması).
  const ARCHETYPES = {
    noi: LOW_BLOCK_TABLE,
    eve: TRUMPET_TABLE,
    aether: LATTICE_ARMCHAIR,
    akasha: SOFT_BENCH,
    loon: SPHERE_CLUSTER_ARMCHAIR,
    araxys: LOZENGE_BENCH,
    lusio: ANGULAR_FOLD_CHAIR,
    botenobo: LAYERED_PEDESTAL,
    bouche: SOFT_WEDGE_CHAIR,
    limbus: ZIGZAG_SLAB,
    lume: LOOP_FRAME,
    nats: SOFT_BENCH,
    pine: TALL_LATTICE_VASE,
    wave: WAVY_SHELF,
    yasuke: TWISTED_RIBBON,
    dezakse: STACKED_RINGS,
  };

  /*
   * Müşterinin gönderdiği gerçek ürün render'larından (PDF, arka planı
   * beyaz stüdyo fotoğrafı) offline çıkarılmış nokta bulutları. Her PDF
   * sayfası `kaynaklar/0N.pdf`de -> yüksek çözünürlükte rasterize edilip
   * beyaza yakın pikseller elenerek (luminance eşiği) siluetin piksel
   * koordinatları toplandı, 1100 noktaya seyreltildi ve normalize edilip
   * (-0.5..0.5 aralığı) base64 Float32Array olarak burada gömüldü - bkz.
   * scratchpad/pdfconv (bu oturuma özel, tema içinde değil). Derinlik (z)
   * piksel parlaklığından türetildi (aydınlık = öne, gölge = arkaya) ince
   * bir kabartma hissi için; fiziksel bir derinlik haritası değil.
   *
   * ARCHETYPES'teki elle çizilmiş kapsül/elipsoit yaklaşıklarının yerini
   * bu 5 anahtar için alıyor - artık müşterinin kendi gönderdiği görsel
   * kullanılıyor, isim/kategori yazısı hiç render edilmiyor (bkz. buildCards).
   */
  const IMAGE_CLOUDS = {
    noi: { b64: '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', bounds: {"x":[-0.3146199891166334,0.31371304190096133],"y":[-0.4838563395610375,0.4811354979140214],"z":[-0.16,0.14870588302612306]} },
    eve: { b64: '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', bounds: {"x":[-0.4821138211382114,0.4698283649503162],"y":[-0.41906052393857274,0.41960252935862696],"z":[0.029490203857421876,0.1424313735961914]} },
    aether: { b64: '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', bounds: {"x":[-0.4790356394129979,0.48427672955974843],"y":[-0.2070230607966457,0.20178197064989517],"z":[-0.0156862735748291,0.14745098114013672]} },
    akasha: { b64: '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', bounds: {"x":[-0.4818696883852691,0.4847025495750708],"y":[-0.23609065155807366,0.23189801699716714],"z":[-0.057098035812377934,-0.02949019432067871]} },
    loon: { b64: '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', bounds: {"x":[-0.4752302318196253,0.47840584312480156],"y":[-0.4320419180692283,0.42870752619879327],"z":[-0.08381239414215089,0.039632320404052734]} },
  };

  function decodeCloudBase64(b64) {
    const bin = atob(b64);
    const buf = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
    return new Float32Array(buf.buffer);
  }

  // Baked tohum bulutundan (~1100 nokta) tam `count` kadar nokta üretir -
  // sampleShell'in kuyruk doldurma tekniğiyle aynı mantık (var olan
  // noktalardan rastgele seçip ince bir jitter ekleyerek çoğaltma), çünkü
  // ekran taneciği bütçesi (2600-8000) baked nokta sayısından çok daha büyük.
  function sampleFromImageCloud(cloud, count) {
    const seed = decodeCloudBase64(cloud.b64);
    const seedCount = seed.length / 3;
    const jitter = 0.008;
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const s = (i < seedCount ? i : Math.floor(Math.random() * seedCount)) * 3;
      arr[i * 3] = seed[s] + (Math.random() - 0.5) * jitter;
      arr[i * 3 + 1] = seed[s + 1] + (Math.random() - 0.5) * jitter;
      arr[i * 3 + 2] = seed[s + 2] + (Math.random() - 0.5) * jitter;
    }
    return { positions: arr, bounds: cloud.bounds };
  }


  function archetypeFor(key, category) {
    const k = (key || '').toLocaleLowerCase('tr-TR');
    if (IMAGE_CLOUDS[k]) return { __cloud: true, cloud: IMAGE_CLOUDS[k] };
    if (ARCHETYPES[k]) return ARCHETYPES[k];
    const c = (category || '').toLocaleLowerCase('tr-TR');
    if (c.indexOf('sehpa') !== -1 || c.indexOf('masa') !== -1) return FOUR_LEG_TABLE;
    return SPHERE_CLUSTER_ARMCHAIR;
  }

  // =====================================================================
  // Deneyim
  // =====================================================================
  let THREE;
  let renderer, scene, camera, cameraYaw, cameraPitch;
  let curve, curveLength;
  let grains, stars, fogGroup, fogMats;
  let texts = {};
  let cards = [];
  let post = null;
  let baseDpr = 1;
  let running = true;
  let disposeVisibility = null;

  const mouse = { x: 9999, y: 9999, tx: 9999, ty: 9999, seen: false };
  const camRotTarget = { x: 0, y: 0 };
  const camRot = { x: 0, y: 0 };
  const cameraState = { u: 0, lookLift: 0, roll: 0, pathYaw: 0, pathPitch: 0 };
  let upAxis, rightAxis;

  const scroll = { target: 0, current: 0 };

  // Kapıdan girildikten sonra kendi kendine oynayan açılışın zamanlaması.
  // INTRO_PROGRESS, açılışın ulaştığı sanal kaydırma ilerlemesi: kum
  // tepeleri (0 - 0.085 bandı) yükselmiş, açılış başlığı (0.005 - 0.06)
  // tamamen belirmiş ama daha dağılmaya başlamamış oluyor.
  const INTRO_MS = 2600;
  const INTRO_DELAY_MS = 400;
  const INTRO_PROGRESS = 0.075;
  let introStart = 0;

  let started = false;
  let clockStart = 0;

  const tmpV = { a: null, b: null, c: null };

  function build() {
    THREE = window.THREE;
    tmpV.a = new THREE.Vector3();
    tmpV.b = new THREE.Vector3();
    tmpV.c = new THREE.Vector3();
    upAxis = new THREE.Vector3(0, 1, 0);
    rightAxis = new THREE.Vector3(1, 0, 0);

    const width = window.innerWidth;
    const height = window.innerHeight;
    // Dolgu-hızı sınırlı olduğumuz için piksel oranı en etkili kaldıraç:
    // maliyet karesiyle ölçekleniyor. Adaptif denetleyici buradan aşağı
    // inebilir, rahatsa geri çıkar.
    baseDpr = Math.min(utils.capDPR(1.5), QUALITY === 'high' ? 1.0 : 0.8);
    const dpr = baseDpr;

    renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false, powerPreference: 'high-performance' });
    renderer.setPixelRatio(dpr);
    renderer.setSize(width, height);
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.setClearColor(0x050505, 1);
    canvasMount.appendChild(renderer.domElement);

    scene = new THREE.Scene();

    camera = new THREE.PerspectiveCamera(CFG.fov, width / height, 0.1, 250);
    camera.position.set(0, 0, 0);
    cameraPitch = new THREE.Object3D();
    cameraYaw = new THREE.Object3D();
    cameraPitch.add(camera);
    cameraYaw.add(cameraPitch);
    scene.add(cameraYaw);

    curve = new THREE.CatmullRomCurve3(
      CFG.path.map((p) => new THREE.Vector3(p[0], p[1], p[2])),
      true,
      'catmullrom',
      0.5
    );
    curveLength = curve.getLength();

    // Yaw + pitch iki ayrı düğüm olarak iç içe - kameranın kendi
    // cameraYaw/cameraPitch rig'iyle birebir aynı bileşim sırası. Tek bir
    // düğümde rotation.x/y birlikte ayarlansa Euler bileşim sırası
    // kameranınkiyle örtüşmez ve pitch değiştiğinde (kara delik sırasında
    // kamera 10+ derece yukarı bakar) anlatı öğeleri kadrajın dışına düşer.
    narrative = new THREE.Object3D();
    narrativePitch = new THREE.Object3D();
    narrative.add(narrativePitch);
    scene.add(narrative);

    buildGrains(width, height, dpr);
    buildStars(width, height, dpr);
    buildFog();
    buildTexts();
    buildCards(width, height, dpr);
    buildPost(width, height, dpr);

    resize();

    // ?cinedebug ile sahne nesnelerini konsola aç - ayar yaparken gerekiyor.
    if (location.search.indexOf('cinedebug') !== -1) {
      window.__cine = { renderer, scene, camera, cameraYaw, curve, grains, stars, fogMats, post, cards, texts, narrative, scroll, cameraState, applyProgress };
    }
  }

  // ---------------------------------------------------------------------
  // Kum taneleri
  // ---------------------------------------------------------------------
  function buildGrains(width, height, dpr) {
    const count = CFG.grains;
    const half = CFG.field / 2;

    const start = new Float32Array(count * 3);
    const target = new Float32Array(count * 3);
    const normals = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    const rnd = new Float32Array(count);
    const rnd2 = new Float32Array(count);
    const heights = new Float32Array(count);
    const noises = new Float32Array(count * 3);

    // Uçuş koridoru maskesi için eğriyi kabaca örnekle.
    const ring = [];
    for (let s = 0; s < 72; s++) {
      const pt = curve.getPointAt(s / 72, new THREE.Vector3());
      ring.push(pt.x, pt.z);
    }
    const corridorAt = (x, z) => {
      let best = Infinity;
      for (let s = 0; s < ring.length; s += 2) {
        const dx = x - ring[s];
        const dz = z - ring[s + 1];
        const d = dx * dx + dz * dz;
        if (d < best) best = d;
      }
      return 0.24 + 0.76 * smoothstep(2.0, 9.5, Math.sqrt(best));
    };

    const eps = 0.55;
    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      const x = (Math.random() - 0.5) * CFG.field;
      const z = (Math.random() - 0.5) * CFG.field;
      const r = Math.random();

      const corridor = corridorAt(x, z);
      const surface = duneSurface(x, z, corridor);
      // Tanecikler yüzeyin hemen altında toplanır, aşağı doğru seyrelir.
      // Böylece üstte keskin bir sırt çizgisi, altta yumuşak bir gövde olur.
      const y = surface - Math.pow(r, 1.7) * 0.34;

      target[i3] = x;
      target[i3 + 1] = y;
      target[i3 + 2] = z;

      // Başlangıç: aynı xz üzerinde düz bir zemin. uProgress 0 -> 1 ile
      // tanecikler bu düzlemden kum tepelerine yükselir.
      start[i3] = x;
      start[i3 + 1] = -2.5 - r * 0.2;
      start[i3 + 2] = z;

      // Normal: yükseklik alanının sonlu farklarla gradyanı.
      const hx = duneSurface(x + eps, z, corridor) - duneSurface(x - eps, z, corridor);
      const hz = duneSurface(x, z + eps, corridor) - duneSurface(x, z - eps, corridor);
      const nx = -hx;
      const ny = 2 * eps;
      const nz = -hz;
      const len = Math.hypot(nx, ny, nz) || 1;
      normals[i3] = nx / len;
      normals[i3 + 1] = ny / len;
      normals[i3 + 2] = nz / len;

      sizes[i] = Math.pow(Math.random(), 2.2);
      rnd[i] = Math.random();
      rnd2[i] = Math.random();
      // Renk karışımı için normalize yükseklik (0 = çukur, 1 = sırt).
      heights[i] = clamp01((y - DUNE_BASE) / DUNE_AMP);

      // Zamandan bağımsız gürültüler: aynalama ve kara delik gecikmeleri ile
      // tünel kabuğu kalınlığı. Vertex shader'da örneklenirlerse kare başına
      // yüz binlerce simplex çağrısı demek oluyordu.
      noises[i3] = noise2(x * 0.18, z * 0.18);
      noises[i3 + 1] = noise2(x * 0.14 + 31.7, z * 0.14);
      noises[i3 + 2] = noise2(x * 0.2 + 81.3, y * 0.2);
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(start, 3));
    geo.setAttribute('aPositionTarget', new THREE.BufferAttribute(target, 3));
    geo.setAttribute('aNormal', new THREE.BufferAttribute(normals, 3));
    geo.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
    geo.setAttribute('aRnd', new THREE.BufferAttribute(rnd, 1));
    geo.setAttribute('aRnd2', new THREE.BufferAttribute(rnd2, 1));
    geo.setAttribute('aHeight', new THREE.BufferAttribute(heights, 1));
    geo.setAttribute('aNoise', new THREE.BufferAttribute(noises, 3));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, -2, 0), 120);

    const mat = new THREE.ShaderMaterial({
      vertexShader: GRAIN_VERTEX,
      fragmentShader: GRAIN_FRAGMENT,
      uniforms: {
        uResolution: { value: new THREE.Vector2(width * dpr, height * dpr) },
        uSize: { value: 0.029 },
        uTime: { value: 0 },
        uProgress: { value: 0 },
        uSplitProgress: { value: 0 },
        uMirrorGap: { value: 7 },
        uDrift: { value: 1 },
        uFall: { value: 1 },
        uMouse: { value: new THREE.Vector2(9999, 9999) },
        uMouseRadius: { value: 0.22 },
        uMouseStrength: { value: 0.18 },
        uMouseDepthFalloff: { value: 1 },
        uBlackHoleProgress: { value: 0 },
        uBlackHoleCenter: { value: new THREE.Vector3(0, 1.2, 0) },
        uBlackHoleAxis: { value: new THREE.Vector3(0, 0, -1) },
        uBlackHoleRadius: { value: 44.5 },
        uBlackHoleSpin: { value: 1.15 },
        uBlackHoleDepth: { value: 32.0 },
        uBlackHoleTunnelRadius: { value: 3.1 },
        uBlackHoleTunnelThickness: { value: 2.3 },
        uBlackHoleOrbitTime: { value: 0.1 },
        uBlackHoleSpiralPull: { value: 0.172 },
        uFieldMin: { value: new THREE.Vector2(-half + 3, -half + 3) },
        uFieldMax: { value: new THREE.Vector2(half - 3, half - 3) },
        uEdgeFadeWidth: { value: 6.5 },
        uColorA: { value: new THREE.Color(COLORS.a) },
        uColorB: { value: new THREE.Color(COLORS.b) },
        uOpacity: { value: 0 },
        uAlpha: { value: 2 },
        /*
         * Referans, sahneyi adaptif parlaklık ölçen bir tone mapping
         * geçişiyle otomatik pozluyordu (AdaptiveLuminancePass). Burada
         * sabit pozlama var, o yüzden tanecik parlaklığı doğrudan
         * yükseltildi - aksi halde kum neredeyse görünmez kalıyor.
         */
        uGlow: { value: 9.5 },
        uCore: { value: 0.35 },
        uCoreSoft: { value: 0.06 },
        uHalo: { value: 0.55 },
        uHaloSoft: { value: 0.25 },
        uHaloStrength: { value: 0.6 },
        uEdge: { value: 0.62 },
        uEdgeSoft: { value: 0.12 },
        uLightDir: { value: new THREE.Vector3(0.4, 0.9, 0.2).normalize() },
        uBase: { value: 0.01 },
        uDiffuse: { value: 0.6 },
        uRim: { value: 0.35 },
        uSpec: { value: 1.2 },
        uSpecPow: { value: 40 },
        uLightWrap: { value: 0.25 },
      },
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
      depthTest: false,
    });

    grains = new THREE.Points(geo, mat);
    grains.frustumCulled = false;
    grains.renderOrder = 1;
    scene.add(grains);
  }

  // ---------------------------------------------------------------------
  // Yıldızlar
  // ---------------------------------------------------------------------
  function buildStars(width, height, dpr) {
    const count = CFG.stars;
    const pos = new Float32Array(count * 3);
    const rnd = new Float32Array(count);
    const rnd2 = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = 26 + Math.random() * 44;
      pos[i * 3] = Math.cos(a) * r;
      pos[i * 3 + 1] = 3 + Math.pow(Math.random(), 0.7) * 26;
      pos[i * 3 + 2] = Math.sin(a) * r;
      rnd[i] = Math.random();
      rnd2[i] = Math.random();
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aRnd', new THREE.BufferAttribute(rnd, 1));
    geo.setAttribute('aRnd2', new THREE.BufferAttribute(rnd2, 1));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 90);

    const mat = new THREE.ShaderMaterial({
      vertexShader: STAR_VERTEX,
      fragmentShader: STAR_FRAGMENT,
      uniforms: {
        uResolution: { value: new THREE.Vector2(width * dpr, height * dpr) },
        uSize: { value: 0.024 },
        uTime: { value: 0 },
        uProgress: { value: 0 },
        uOpacity: { value: 0 },
        uColor: { value: new THREE.Color(COLORS.glow) },
      },
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
      depthTest: false,
    });

    stars = new THREE.Points(geo, mat);
    stars.frustumCulled = false;
    stars.renderOrder = 0;
    scene.add(stars);
  }

  // ---------------------------------------------------------------------
  // Ürün silueti - kumdan/dağınık taneciklerden bir mobilya kütlesine
  // toplanan nokta bulutu. Malzeme dune ile aynı shader'ı (aynı core/halo
  // parıltısı, aynı additive harman) kullanır; sadece hedef geometri
  // değişir - "aynı tasarım ve animasyonla" isteğinin karşılığı bu.
  // ---------------------------------------------------------------------
  function buildSilhouetteGroup(parts, width, height, dpr) {
    const count = CFG.silhouetteGrains;
    const { positions: target, bounds } = parts && parts.__cloud
      ? sampleFromImageCloud(parts.cloud, count)
      : sampleShell(parts, count);
    const start = new Float32Array(count * 3);
    const normals = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    const rnd = new Float32Array(count);
    const rnd2 = new Float32Array(count);
    const heights = new Float32Array(count);
    const noises = new Float32Array(count * 3);

    const cx = (bounds.x[0] + bounds.x[1]) / 2;
    const cy = (bounds.y[0] + bounds.y[1]) / 2;
    const cz = (bounds.z[0] + bounds.z[1]) / 2;
    const extent = Math.max(bounds.x[1] - bounds.x[0], bounds.y[1] - bounds.y[0], bounds.z[1] - bounds.z[0]);
    const scatterR = extent * 1.6 + 0.6;
    const yMin = bounds.y[0];
    const yMax = bounds.y[1];

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      const tx = target[i3];
      const ty = target[i3 + 1];
      const tz = target[i3 + 2];

      // Başlangıç: hedef kütlenin etrafında dağılmış toz bulutu - kum
      // tanelerinin bir siluete toplanması hissi için.
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = scatterR * (0.55 + Math.random() * 0.6);
      start[i3] = cx + r * Math.sin(phi) * Math.cos(theta);
      start[i3 + 1] = cy + r * Math.cos(phi) * 0.6;
      start[i3 + 2] = cz + r * Math.sin(phi) * Math.sin(theta);

      // Normal yaklaşık: merkezden hedefe doğru yön - gerçek yüzey normali
      // değil ama örtüşen elipsoit kümesinde ışıklandırma için yeterli.
      const nx = tx - cx;
      const ny = ty - cy + 0.35;
      const nz = tz - cz;
      const len = Math.hypot(nx, ny, nz) || 1;
      normals[i3] = nx / len;
      normals[i3 + 1] = ny / len;
      normals[i3 + 2] = nz / len;

      sizes[i] = Math.pow(Math.random(), 1.8);
      rnd[i] = Math.random();
      rnd2[i] = Math.random();
      heights[i] = clamp01((ty - yMin) / Math.max(yMax - yMin, 1e-4));
      noises[i3] = Math.random();
      noises[i3 + 1] = Math.random();
      noises[i3 + 2] = Math.random();
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(start, 3));
    geo.setAttribute('aPositionTarget', new THREE.BufferAttribute(target, 3));
    geo.setAttribute('aNormal', new THREE.BufferAttribute(normals, 3));
    geo.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
    geo.setAttribute('aRnd', new THREE.BufferAttribute(rnd, 1));
    geo.setAttribute('aRnd2', new THREE.BufferAttribute(rnd2, 1));
    geo.setAttribute('aHeight', new THREE.BufferAttribute(heights, 1));
    geo.setAttribute('aNoise', new THREE.BufferAttribute(noises, 3));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(cx, cy, cz), scatterR + 1);

    const mat = grains.material.clone();
    mat.uniforms = THREE.UniformsUtils.clone(grains.material.uniforms);
    mat.uniforms.uResolution.value = new THREE.Vector2(width * dpr, height * dpr);
    // Dune'dan daha küçük tanecik + daha az parıltı: aksi halde bloom
    // örtüşen elipsoitler arasındaki boşlukları da yayıp tek bulanık
    // kütleye dönüştürüyor, siluet okunmaz oluyordu.
    mat.uniforms.uSize.value = 0.019;
    mat.uniforms.uOpacity.value = 0;
    mat.uniforms.uGlow.value = 7.5;
    mat.uniforms.uFall.value = 0;
    mat.uniforms.uDrift.value = 0.3;
    mat.uniforms.uEdgeFadeWidth.value = 0.001;
    mat.uniforms.uFieldMin.value.set(-999, -999);
    mat.uniforms.uFieldMax.value.set(999, 999);
    mat.uniforms.uBlackHoleRadius.value = 0.0001;
    // Halo'yu daralt: geniş bir halo, örtüşen elipsoitler arasındaki
    // boşluğu da dolduruyor ve siluet ayrıntıları (kol, küçük top) kayboluyor.
    mat.uniforms.uHalo.value = 0.42;
    mat.uniforms.uHaloStrength.value = 0.4;

    const points = new THREE.Points(geo, mat);
    points.frustumCulled = false;
    points.renderOrder = 2;
    return { points, bounds };
  }

  // ---------------------------------------------------------------------
  // Sis
  // ---------------------------------------------------------------------
  /** Döşenebilir fbm gürültüsü: R/G/B üç farklı oktav kümesi taşır. */
  function buildNoiseTexture(size) {
    const hash = (x, y, seed) => {
      let n = Math.imul(x, 374761393) + Math.imul(y, 668265263) + Math.imul(seed, 1442695041);
      n = Math.imul(n ^ (n >>> 13), 1274126177);
      return ((n ^ (n >>> 16)) >>> 0) / 4294967295;
    };
    const fade = (t) => t * t * t * (t * (t * 6 - 15) + 10);
    // Periyodu f olan değer gürültüsü: [0,1) aralığında sorunsuz döşenir.
    const tile = (u, v, f, seed) => {
      const x = u * f;
      const y = v * f;
      const xi = Math.floor(x);
      const yi = Math.floor(y);
      const tx = fade(x - xi);
      const ty = fade(y - yi);
      const x0 = ((xi % f) + f) % f;
      const y0 = ((yi % f) + f) % f;
      const x1 = (x0 + 1) % f;
      const y1 = (y0 + 1) % f;
      const a = hash(x0, y0, seed);
      const b = hash(x1, y0, seed);
      const c = hash(x0, y1, seed);
      const d = hash(x1, y1, seed);
      return a * (1 - tx) * (1 - ty) + b * tx * (1 - ty) + c * (1 - tx) * ty + d * tx * ty;
    };
    const fbmTile = (u, v, seed, base, oct) => {
      let amp = 0.5;
      let f = base;
      let sum = 0;
      let norm = 0;
      for (let i = 0; i < oct; i++) {
        sum += amp * tile(u, v, f, seed + i);
        norm += amp;
        amp *= 0.5;
        f *= 2;
      }
      return sum / norm;
    };

    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const img = ctx.createImageData(size, size);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const u = x / size;
        const v = y / size;
        const i = (y * size + x) * 4;
        img.data[i] = fbmTile(u, v, 1, 6, 6) * 255;
        img.data[i + 1] = fbmTile(u, v, 37, 10, 5) * 255;
        img.data[i + 2] = fbmTile(u, v, 91, 4, 4) * 255;
        img.data[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);

    const tex = new THREE.CanvasTexture(canvas);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    return tex;
  }

  function buildFog() {
    fogGroup = new THREE.Group();
    scene.add(fogGroup);

    const noiseTex = buildNoiseTexture(512);

    const geo = new THREE.PlaneGeometry(9, 6, 1, 1);
    const makeMat = (opacity, density, speed, noiseScale) =>
      new THREE.ShaderMaterial({
        vertexShader: FOG_VERTEX,
        fragmentShader: FOG_FRAGMENT,
        uniforms: {
          uTime: { value: 0 },
          uOpacity: { value: opacity },
          uDensity: { value: density },
          uSpeed: { value: speed },
          uNoiseScale: { value: noiseScale },
          uColorDark: { value: new THREE.Color('#000000') },
          uColorLight: { value: new THREE.Color(COLORS.glow) },
          uMouse: { value: new THREE.Vector2(9999, 9999) },
          uMouseRadius: { value: 0.35 },
          uMouseStrength: { value: 0.35 },
          uNoiseTex: { value: noiseTex },
        },
        blending: THREE.AdditiveBlending,
        transparent: true,
        depthWrite: false,
        depthTest: false,
        side: THREE.DoubleSide,
      });

    const front = makeMat(0.0, 0.36, 0.111, 2.5);
    const back = makeMat(0.0, 0.24, 0.07, 2.5);
    fogMats = [front, back];

    const frontMesh = new THREE.Mesh(geo, front);
    frontMesh.position.set(0, 0, 0);
    frontMesh.renderOrder = 3;
    const backMesh = new THREE.Mesh(geo, back);
    backMesh.position.set(0, 0, -1.5);
    backMesh.scale.set(1.2, 1.2, 1);
    backMesh.renderOrder = 0;

    fogGroup.add(backMesh, frontMesh);
  }

  // ---------------------------------------------------------------------
  // Anlatı rig'i
  //
  // İlk denemede yazılar dünya uzayına, kamera eğrisinin ilerisindeki bir
  // noktaya konmuştu. Eğrinin yarıçapı ~6 birim olduğu için 8-9 birim
  // ileriye konan bir yazı kadrajın tamamen dışına savruluyordu; sahne
  // boyunca hiçbir kelime düzgün çerçevelenmiyordu.
  //
  // Çözüm: anlatıya ait her şey (kelimeler, ürün siluetleri) kameranın
  // konumunu, YOL açısını (yaw) VE yol eğimini (pitch) takip eden - ama
  // fare salınımını takip etmeyen - bir rig'in çocuğu. Yaklaşma hissi
  // öğenin yerel Z mesafesini bölüm boyunca kısaltarak veriliyor; uçuş
  // hissi zaten dünyaya sabit kum, yıldız ve sisten geliyor.
  //
  // İki ayrı düğüm (narrative -> narrativePitch) gerekli: kamera da
  // cameraYaw -> cameraPitch şeklinde iç içe iki obje. Tek bir objede
  // rotation.x ve rotation.y birlikte ayarlansa Euler bileşim sırası
  // kameranınkiyle örtüşmez. Kara delik sahnesinde kamera ~12° yukarı
  // baktığında (cameraState.lookLift) bu fark edilir hale geliyordu:
  // ürün siluetleri "düz ileri" konumlarında kalıp kadrajın altına
  // düşüyordu - pitch de eşlenince sorun ortadan kalktı.
  // ---------------------------------------------------------------------
  let narrative, narrativePitch;

  function makeTextMesh(text, opts) {
    const o = Object.assign({ worldWidth: 2.6, tracking: 0.24, weight: 500, grain: 1 }, opts || {});
    const built = textTexture(THREE, text, { tracking: o.tracking, weight: o.weight, size: 108 });
    const h = o.worldWidth / built.aspect;

    const mat = new THREE.ShaderMaterial({
      vertexShader: TEXT_VERTEX,
      fragmentShader: TEXT_FRAGMENT,
      uniforms: {
        uMap: { value: built.texture },
        uColor: { value: new THREE.Color(COLORS.glow) },
        uReveal: { value: 0 },
        uOpacity: { value: 1 },
        uTime: { value: 0 },
        uGrain: { value: o.grain },
      },
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    });

    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(o.worldWidth, h, 1, 1), mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = 10;
    mesh.userData.worldWidth = o.worldWidth;
    narrativePitch.add(mesh);
    return mesh;
  }

  /*
   * Bir öğenin verilen mesafede kadraja sığması için gereken ölçek.
   *
   * Sabit genişlikler yalnızca geniş ekranlarda çalışıyordu: dikey bir
   * telefon ekranında yatay görüş açısı çok daralıyor ve "IMAGINATION"
   * gibi uzun kelimeler iki yandan taşıyordu. Bu hesap her en-boy oranında
   * ve her mesafede taşmayı imkânsız kılar; geniş ekranlarda sonuç zaten
   * 1 olduğu için tasarımı değiştirmez.
   */
  const TAN_HALF_FOV = Math.tan((CFG.fov * 0.5 * Math.PI) / 180);

  function fitScale(worldWidth, distance) {
    if (!worldWidth) return 1;
    const visible = 2 * distance * TAN_HALF_FOV * (camera ? camera.aspect : 1.78);
    return Math.min(1, (visible * 0.86) / worldWidth);
  }

  const BEATS = {
    // [belirme başı, belirme sonu, dağılma başı, dağılma sonu]
    hero: [0.005, 0.06, 0.1, 0.16],
    w1: [0.19, 0.25, 0.3, 0.355],
    w2: [0.37, 0.43, 0.475, 0.53],
    w3: [0.545, 0.6, 0.635, 0.685],
    w4: [0.67, 0.72, 0.755, 0.8],
    closing: [0.962, 0.995, 1.2, 1.3],
  };

  // Her yazının rig içindeki [uzak, yakın] Z mesafesi ve yükseklik.
  const DEPTHS = {
    hero: [6.0, 4.6, 0.34],
    w1: [8.5, 3.2, 0.5],
    w2: [8.5, 3.2, 0.5],
    w3: [8.5, 3.4, 0.62],
    w4: [8.5, 3.2, 0.5],
    closing: [6.0, 4.4, 0.34],
  };

  // p (kaydırma) -> u (kamera eğrisi parametresi)
  const uOf = (p) => clamp01((p - 0.045) / 0.93);

  function buildTexts() {
    const data = root.dataset;
    const defs = [
      ['hero', data.cineHero, { worldWidth: 2.4, tracking: 0.42, weight: 400 }],
      ['w1', data.cineWord1, { worldWidth: 2.9, tracking: 0.32, weight: 500 }],
      ['w2', data.cineWord2, { worldWidth: 3.2, tracking: 0.26, weight: 500 }],
      ['w3', data.cineWord3, { worldWidth: 3.2, tracking: 0.26, weight: 500 }],
      ['w4', data.cineWord4, { worldWidth: 2.9, tracking: 0.32, weight: 500 }],
      ['closing', data.cineClosing, { worldWidth: 3.0, tracking: 0.2, weight: 400 }],
    ];

    defs.forEach(([key, text, opts]) => {
      if (!text) return;
      texts[key] = makeTextMesh(text, opts);
    });
  }

  // ---------------------------------------------------------------------
  // Ürün siluetleri - referanstaki ödül galerisinin karşılığı.
  //
  // Önceki sürüm burada fotoğraf düzlemleri açıyordu; müşteri gerçek ürün
  // fotoğrafı istemediği için (dağ/kara delik gibi tanecik siluetleriyle
  // aynı dilde kalsın istiyor) her kart artık kendi ürün silueti - bkz.
  // buildSilhouetteGroup / ARCHETYPES. Beş kart (noi/eve/aether/akasha/loon)
  // artık müşterinin gönderdiği gerçek ürün render'larından çıkarılmış
  // IMAGE_CLOUDS nokta bulutlarını kullanıyor, geri kalanı hâlâ elle
  // çizilmiş ARCHETYPES kapsül/elipsoit yaklaşıklarında (bkz. archetypeFor).
  // ---------------------------------------------------------------------
  function buildCards(width, height, dpr) {
    if (!galleryMount) return;
    const items = [...galleryMount.querySelectorAll('[data-cine-product]')];
    if (!items.length) return;

    // Aralarında yeterli boşluk olmalı: taneciklerden oluşan bir siluet,
    // fotoğraf kartının aksine yarı saydam - iki siluet aynı anda
    // görünürse üst üste binip birbirine karışıyor. Başlangıç (0.82),
    // kara delik tünelinin tamamen söndüğü noktadan (0.815) sonra - aksi
    // halde ilk kart tünelin parıltısıyla üst üste biniyordu.
    const span = [0.82, 0.95];

    items.forEach((item, i) => {
      const t = items.length === 1 ? 0.5 : i / (items.length - 1);
      const p = lerp(span[0], span[1], t);

      const group = new THREE.Group();
      narrativePitch.add(group);

      const { points, bounds } = buildSilhouetteGroup(archetypeFor(item.dataset.key, item.dataset.category), width, height, dpr);
      group.add(points);

      // Kadraja sığdırma için gerçek taban genişliği: sabit bir sayı
      // (ör. 1.1) her arketip için yanlıştı - Araxys/Limbus gibi geniş
      // formlar dar mobil ekranlarda kadrajın dışına taşıyordu. Y
      // dönüşünden bağımsız garanti bir üst sınır için x/z köşegeni
      // kullanılıyor (grup en fazla bu kadar döner, bkz. aşağıdaki 0.24 rad).
      const xSpan = bounds.x[1] - bounds.x[0];
      const zSpan = bounds.z[1] - bounds.z[0];
      const footprint = Math.hypot(xSpan, zSpan);

      // Müşteri isteği: kartların altında mobilya adı/kategorisi artık
      // hiç yazmıyor - sadece nokta bulutu silueti görünüyor.
      group.frustumCulled = false;
      cards.push({ group, mat: points.material, points, labelMesh: null, p, side: i % 2 === 0 ? 1 : -1, footprint, href: item.dataset.href || '' });
    });
  }

  // ---------------------------------------------------------------------
  // Post-processing (bright pass -> ayrılabilir bulanıklaştırma -> birleştirme)
  // ---------------------------------------------------------------------
  function buildPost(width, height, dpr) {
    /*
     * Referans HalfFloat bir frame buffer kullanıyordu ve additive
     * birikimin tüm dinamik aralığını koruyordu. Bu tema tümleşik
     * ekran kartlarında da çalışmak zorunda: ölçtüğümüzde bir Intel UHD
     * 620'de RGBA16F hedef, aynı sahneyi 8-bit hedefe göre 2.3 kat
     * pahalıya çiziyordu (70 ms'ye karşı 31 ms). İş tamamen dolgu-hızı
     * sınırlı olduğu için 8-bit hedef + daha yüksek tanecik parlaklığı
     * (uGlow) görsel olarak neredeyse aynı sonucu yarı maliyetle veriyor.
     */
    const opts = {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      stencilBuffer: false,
    };

    const bw = Math.max(2, Math.round((width * dpr) / 4));
    const bh = Math.max(2, Math.round((height * dpr) / 4));

    const rtScene = new THREE.WebGLRenderTarget(Math.round(width * dpr), Math.round(height * dpr), {
      ...opts,
      depthBuffer: true,
    });
    const rtA = new THREE.WebGLRenderTarget(bw, bh, { ...opts, depthBuffer: false });
    const rtB = new THREE.WebGLRenderTarget(bw, bh, { ...opts, depthBuffer: false });

    const quadScene = new THREE.Scene();
    const quadCam = new THREE.Camera();
    const quadGeo = new THREE.PlaneGeometry(2, 2);
    const quad = new THREE.Mesh(quadGeo, null);
    quad.frustumCulled = false;
    quadScene.add(quad);

    const bright = new THREE.ShaderMaterial({
      vertexShader: QUAD_VERTEX,
      fragmentShader: BRIGHT_FRAGMENT,
      uniforms: {
        uMap: { value: rtScene.texture },
        uThreshold: { value: 0.06 },
        uSmoothing: { value: 0.14 },
      },
      depthTest: false,
      depthWrite: false,
    });

    const blur = new THREE.ShaderMaterial({
      vertexShader: QUAD_VERTEX,
      fragmentShader: BLUR_FRAGMENT,
      uniforms: {
        uMap: { value: null },
        uDirection: { value: new THREE.Vector2() },
      },
      depthTest: false,
      depthWrite: false,
    });

    const composite = new THREE.ShaderMaterial({
      vertexShader: QUAD_VERTEX,
      fragmentShader: COMPOSITE_FRAGMENT,
      uniforms: {
        uScene: { value: rtScene.texture },
        uBloom: { value: rtA.texture },
        uIntensity: { value: 0.95 },
        uExposure: { value: 3.6 },
        uVignette: { value: 0.6 },
      },
      depthTest: false,
      depthWrite: false,
    });

    post = {
      rtScene,
      rtA,
      rtB,
      quadScene,
      quadCam,
      quad,
      bright,
      blur,
      composite,
      bw,
      bh,
      resize(w, h, ratio) {
        const sw = Math.round(w * ratio);
        const sh = Math.round(h * ratio);
        rtScene.setSize(sw, sh);
        post.bw = Math.max(2, Math.round(sw / 4));
        post.bh = Math.max(2, Math.round(sh / 4));
        rtA.setSize(post.bw, post.bh);
        rtB.setSize(post.bw, post.bh);
      },
      passes: QUALITY === 'high' ? [1.0, 2.2, 3.6] : [1.4, 2.8],
      render() {
        renderer.setRenderTarget(rtScene);
        renderer.render(scene, camera);

        quad.material = bright;
        renderer.setRenderTarget(rtA);
        renderer.render(quadScene, quadCam);

        quad.material = blur;
        const passes = post.passes;
        for (let i = 0; i < passes.length; i++) {
          blur.uniforms.uMap.value = rtA.texture;
          blur.uniforms.uDirection.value.set(passes[i] / post.bw, 0);
          renderer.setRenderTarget(rtB);
          renderer.render(quadScene, quadCam);

          blur.uniforms.uMap.value = rtB.texture;
          blur.uniforms.uDirection.value.set(0, passes[i] / post.bh);
          renderer.setRenderTarget(rtA);
          renderer.render(quadScene, quadCam);
        }

        quad.material = composite;
        renderer.setRenderTarget(null);
        renderer.render(quadScene, quadCam);
      },
    };
  }

  // ---------------------------------------------------------------------
  // Yolculuk durumu
  //
  // Referans GSAP timeline'larını "scrub" ediyordu. Burada her değeri
  // doğrudan p'den (0..1 kaydırma ilerlemesi) hesaplıyoruz: aynı uniform'a
  // yazan iki ayrı parça olmadığı için ileri/geri kaydırma tamamen
  // simetrik ve sıraya bağımsız oluyor.
  // ---------------------------------------------------------------------
  function applyProgress(p) {
    const gu = grains.material.uniforms;
    const su = stars.material.uniforms;

    // 1. Açılış: kum düz zeminden tepelere yükselir.
    gu.uProgress.value = smoothstep(0.0, 0.085, p);
    gu.uOpacity.value = smoothstep(0.0, 0.03, p);

    // 2. Yazılar. reveal 0->1 belirir, 1->2 dağılır. Rig içinde bölüm
    //    boyunca yaklaşırlar (uzak -> yakın Z).
    Object.keys(BEATS).forEach((key) => {
      const mesh = texts[key];
      if (!mesh) return;
      const b = BEATS[key];
      const d = DEPTHS[key];
      mesh.material.uniforms.uReveal.value = smoothstep(b[0], b[1], p) + smoothstep(b[2], b[3], p);
      const t = clamp01((p - b[0]) / (b[3] - b[0]));
      const z = lerp(d[0], d[1], easeInOut(t));
      const fit = fitScale(mesh.userData.worldWidth, z);
      mesh.position.set(0, d[2] * fit, -z);
      mesh.scale.setScalar(fit);
    });

    // 4. Yıldızlar: koridor kapanırken gökyüzü açılır.
    su.uProgress.value = smoothstep(0.56, 0.76, p);
    su.uOpacity.value = smoothstep(0.56, 0.76, p) * 0.9 * (1 - 0.5 * smoothstep(0.96, 1.0, p));

    // 5. Aynalama: kum tepeleri yukarıda da belirir, koridor kapanır.
    //    Ürün siluetleri belirmeden (0.83+) tamamen kapanmış olmalı -
    //    aksi halde aynı ekran bölgesinde iki tanecik efekti üst üste
    //    binip siluetin okunmasını zorlaştırıyordu.
    gu.uSplitProgress.value = smoothstep(0.6, 0.74, p) * (1 - smoothstep(0.77, 0.81, p));

    // 6. Kara delik tüneli - dördüncü kelime ile birlikte. Ürün siluetleri
    //    başlamadan önce tünel tamamen sönmüş olmalı: aynı parıltı dilini
    //    paylaştıkları için üst üste kalırlarsa siluet ayırt edilemiyor.
    gu.uBlackHoleProgress.value = smoothstep(0.68, 0.76, p) * (1 - smoothstep(0.775, 0.815, p));

    // 7. Sis.
    const fog = 0.005 + 0.015 * Math.sin(clamp01((p - 0.08) / 0.92) * Math.PI);
    fogMats[0].uniforms.uOpacity.value = fog;
    fogMats[1].uniforms.uOpacity.value = fog * 0.8;

    // 8. Ürün siluetleri: kamera yaklaşırken dağınık tozdan kütleye
    //    toplanırlar (uProgress), geçerken tekrar dağılıp kaybolurlar.
    //    Fotoğraf değil - dune/kara delik ile aynı tanecik malzemesi.
    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      const cu = card.mat.uniforms;
      // Komşu kartlar arası boşluk (bkz. buildCards) yalnızca ~0.0325;
      // pencere ondan dar tutulmalı, yoksa iki siluet aynı anda
      // yarı saydam biçimde üst üste biner.
      const appear = smoothstep(card.p - 0.02, card.p - 0.004, p);
      const leave = smoothstep(card.p + 0.005, card.p + 0.016, p);
      cu.uProgress.value = easeInOut(appear);
      cu.uOpacity.value = appear * (1 - leave);
      // Ayrılırken taneciklerin bir kısmı yükselen kıvılcım gibi dağılsın.
      cu.uFall.value = leave;
      if (card.labelMesh) card.labelMesh.material.uniforms.uReveal.value = appear + leave;
      card.points.visible = cu.uOpacity.value > 0.001;

      const t = clamp01((p - (card.p - 0.02)) / 0.024);
      const z = lerp(6.8, 3.0, easeInOut(t));
      /*
       * Mobilde siluetler yarım kadraj taşıyordu: `fitScale`'e sabit bir
       * genişlik (1.1) veriliyordu ama arketipler çok farklı genişlikte -
       * Araxys/Limbus gibi geniş formlar gerçekte bundan çok daha geniş,
       * dar telefon ekranında hesap yanılıp taşma oluyordu. Artık gerçek
       * taban genişliği (`card.footprint`, bkz. buildCards) kullanılıyor.
       * Mobilde ayrıca fazladan bir küçültme payı (padding) ve daha küçük
       * yanal kaçıklık (sideAmount) veriliyor ki siluet hem biraz daha
       * küçük hem de ekranın ortasına daha yakın dursun.
       */
      const padding = isMobile ? 1.35 : 1.08;
      const sideAmount = isMobile ? 0.06 : 0.22;
      const fit = fitScale(card.footprint * padding, z);
      card.group.position.set(card.side * z * sideAmount * fit, 0.1, -z);
      card.group.rotation.y = -card.side * 0.24 + leave * card.side * 0.4;
      card.group.scale.setScalar(fit);
    }

    // 9. Kamera.
    cameraState.u = uOf(p);
    cameraState.lookLift = smoothstep(0.66, 0.78, p) * 0.5 - smoothstep(0.86, 0.95, p) * 0.5;
    cameraState.roll = smoothstep(0.68, 0.8, p) * 0.07 - smoothstep(0.84, 0.94, p) * 0.07;
    gu.uDrift.value = 1 + smoothstep(0.66, 0.76, p) * 1.2 * (1 - smoothstep(0.78, 0.85, p));
  }

  // ---------------------------------------------------------------------
  // Kamera güncellemesi
  // ---------------------------------------------------------------------
  const camPos = { v: null };
  const camAhead = { v: null };

  function updateCamera() {
    if (!camPos.v) {
      camPos.v = new THREE.Vector3();
      camAhead.v = new THREE.Vector3();
    }
    const u = ((cameraState.u % 1) + 1) % 1;
    curve.getPointAt(u, camPos.v);
    curve.getPointAt((u + 0.035) % 1, camAhead.v);

    cameraYaw.position.copy(camPos.v);

    const dx = camAhead.v.x - camPos.v.x;
    const dy = camAhead.v.y - camPos.v.y + cameraState.lookLift;
    const dz = camAhead.v.z - camPos.v.z;

    const yaw = -Math.atan2(dx, -dz);
    const pitch = Math.atan2(dy, Math.sqrt(dx * dx + dz * dz));
    cameraState.pathYaw = yaw;
    cameraState.pathPitch = pitch;

    camRot.x += (camRotTarget.x - camRot.x) * 0.06;
    camRot.y += (camRotTarget.y - camRot.y) * 0.06;

    cameraYaw.rotation.y = yaw + camRot.y;
    cameraPitch.rotation.x = pitch + camRot.x;
    camera.rotation.z = cameraState.roll;
  }

  /*
   * Anlatı rig'i kameranın konumunu ve YOL açısını izler, fare salınımını
   * izlemez: böylece fare hareketi yazıların üzerinde hafif bir paralaks
   * yaratır ama çerçeveleme hiçbir zaman bozulmaz.
   */
  function updateNarrative() {
    narrative.position.copy(cameraYaw.position);
    narrative.rotation.y = cameraState.pathYaw;
    narrativePitch.rotation.x = cameraState.pathPitch;
  }

  /*
   * Kara delik ekseni her karede kameranın önüne kurulur. Sabit bir dünya
   * noktası kullanmak, yolun neresinde olduğumuza göre tünelin bazen yandan
   * görünmesine yol açıyordu; kameranın bakış eksenine kilitlemek tüneli
   * her zaman ekranın merkezine oturtuyor.
   */
  const bhTmp = { c: null, f: null };

  function updateBlackHole(gu) {
    if (gu.uBlackHoleProgress.value < 0.0005) return;
    if (!bhTmp.c) {
      bhTmp.c = new THREE.Vector3();
      bhTmp.f = new THREE.Vector3();
    }
    // Eksen, senaryonun kamera hedefiyle (yol açısı + bakış kaldırması)
    // kurulur; fare salınımı dışarıda bırakılır, yoksa girdap fareye
    // kilitlenip ekranda hiç kıpırdamaz.
    bhTmp.f
      .set(0, 0, -1)
      .applyAxisAngle(rightAxis, cameraState.pathPitch)
      .applyAxisAngle(upAxis, cameraState.pathYaw);
    bhTmp.c.copy(cameraYaw.position).addScaledVector(bhTmp.f, 7.5);
    gu.uBlackHoleCenter.value.copy(bhTmp.c);
    gu.uBlackHoleAxis.value.copy(bhTmp.f);
  }

  const fogQuat = { q: null, fwd: null };

  function updateFog() {
    if (!fogQuat.q) {
      fogQuat.q = new THREE.Quaternion();
      fogQuat.fwd = new THREE.Vector3();
    }
    camera.getWorldPosition(tmpV.a);
    camera.getWorldQuaternion(fogQuat.q);
    fogQuat.fwd.set(0, 0, -1).applyQuaternion(fogQuat.q);
    fogGroup.position.copy(tmpV.a).addScaledVector(fogQuat.fwd, 4.2);
    fogGroup.quaternion.copy(fogQuat.q);
  }

  // ---------------------------------------------------------------------
  // Kaydırma
  // ---------------------------------------------------------------------
  function readScroll() {
    const rect = track.getBoundingClientRect();
    const total = track.offsetHeight - window.innerHeight;
    scroll.target = total > 0 ? clamp01(-rect.top / total) : 0;
  }

  // ---------------------------------------------------------------------
  // Boyutlandırma
  // ---------------------------------------------------------------------
  function resize() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const dpr = renderer.getPixelRatio();

    renderer.setSize(width, height);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();

    const res = new THREE.Vector2(width * dpr, height * dpr);
    grains.material.uniforms.uResolution.value.copy(res);
    stars.material.uniforms.uResolution.value.copy(res);
    cards.forEach((c) => c.mat.uniforms.uResolution.value.copy(res));
    if (post) post.resize(width, height, dpr);
    resizeTrail();
  }

  // ---------------------------------------------------------------------
  // Render döngüsü
  // ---------------------------------------------------------------------
  /*
   * Adaptif kalite.
   *
   * İlk sürüm bir Intel UHD 620 üzerinde 4 fps veriyordu. Yük büyük ölçüde
   * tanecik sayısı x piksel oranı ile ölçekleniyor, o yüzden referanstaki
   * gibi (setParticleRatio / applyRenderPixelRatio) ikisi de çalışma anında
   * ayarlanıyor: kare süresi hedefin üstüne çıkarsa önce çizim aralığı,
   * sonra piksel oranı kısılır; rahatladığında kademeli olarak geri verilir.
   */
  const perf = { avg: 16.7, last: 0, ratio: 1, pixel: 1, cooldown: 0 };

  function updatePerformance(now) {
    if (!perf.last) {
      perf.last = now;
      return;
    }
    const dt = now - perf.last;
    perf.last = now;
    // Sekme arka plana alındığında veya tarayıcı rAF'ı kıstığında kare
    // aralığı saniyelere çıkıyor; bunu gerçek yavaşlık sanıp kaliteyi
    // düşürmemek için aşırı uzun kareler yok sayılıyor.
    if (dt > 90) return;
    perf.avg = perf.avg * 0.92 + dt * 0.08;

    if (perf.cooldown > 0) {
      perf.cooldown -= 1;
      return;
    }

    let ratio = perf.ratio;
    let pixel = perf.pixel;

    if (perf.avg > 26) {
      if (pixel > 0.55) pixel = Math.max(0.55, pixel - 0.1);
      else if (ratio > 0.3) ratio = Math.max(0.3, ratio - 0.12);
    } else if (perf.avg < 14) {
      if (pixel < 1) pixel = Math.min(1, pixel + 0.06);
      else if (ratio < 1) ratio = Math.min(1, ratio + 0.05);
    }

    if (ratio !== perf.ratio) {
      perf.ratio = ratio;
      const geo = grains.geometry;
      geo.setDrawRange(0, Math.max(1, Math.floor(CFG.grains * ratio)));
      perf.cooldown = 45;
    }
    if (pixel !== perf.pixel) {
      perf.pixel = pixel;
      renderer.setPixelRatio(baseDpr * pixel);
      resize();
      perf.cooldown = 45;
    }
  }

  function render() {
    if (!running) return;

    const now = performance.now();
    updatePerformance(now);
    const time = (performance.now() - clockStart) / 1000;

    readScroll();
    // Lenis benzeri yumuşatma: hedefe doğru kademeli yaklaşım.
    scroll.current += (scroll.target - scroll.current) * 0.085;

    /*
     * Açılış animasyonu.
     *
     * Kapıya basıldığında sahne kaydırma beklemeden kendi kendine oynar:
     * kum düz zeminden tepelere yükselir ve açılış başlığı belirir. Aksi
     * halde p = 0'da başlık henüz görünmediği için kullanıcı kaydırana
     * kadar bomboş bir ekranla karşılaşıyordu. Referans da aynısını
     * yapıyordu (enter -> 2 sn'lik zamanla sürülen reveal).
     *
     * Kaydırma ilerlemesiyle max alınıyor: kullanıcı yukarı dönse bile
     * açılış geri sarılmıyor, aşağı kaydırınca da sorunsuz devralıyor.
     */
    let introP = 0;
    if (introStart) {
      introP = INTRO_PROGRESS * easeInOut(clamp01((now - introStart - INTRO_DELAY_MS) / INTRO_MS));
    }

    applyProgress(Math.max(scroll.current, introP));

    mouse.x += (mouse.tx - mouse.x) * 0.08;
    mouse.y += (mouse.ty - mouse.y) * 0.08;

    const gu = grains.material.uniforms;
    gu.uTime.value = time;
    gu.uMouse.value.set(mouse.x, mouse.y);
    gu.uBlackHoleOrbitTime.value += 0.016 * 0.022;

    stars.material.uniforms.uTime.value = time;

    fogMats.forEach((m) => {
      m.uniforms.uTime.value = time;
      m.uniforms.uMouse.value.set(mouse.x, mouse.y);
    });

    Object.keys(texts).forEach((k) => {
      texts[k].material.uniforms.uTime.value = time;
    });
    cards.forEach((c) => {
      c.mat.uniforms.uTime.value = time;
      c.mat.uniforms.uMouse.value.set(mouse.x, mouse.y);
      if (c.labelMesh) c.labelMesh.material.uniforms.uTime.value = time;
    });

    updateCamera();
    updateNarrative();
    updateBlackHole(gu);
    updateFog();
    drawTrail();

    if (post) post.render();
    else renderer.render(scene, camera);
  }

  function loop() {
    render();
    requestAnimationFrame(loop);
  }

  // ---------------------------------------------------------------------
  // Olaylar
  // ---------------------------------------------------------------------
  function bindEvents() {
    window.addEventListener(
      'mousemove',
      (e) => {
        mouse.tx = (e.clientX / window.innerWidth) * 2 - 1;
        mouse.ty = -(e.clientY / window.innerHeight) * 2 + 1;
        // İlk harekette anında yerine otur: aksi halde 9999'dan gerçek
        // koordinata doğru yavaşça süzülürken tüm alan boyunca sahte bir
        // itiş dalgası geçiyor.
        if (!mouse.seen) {
          mouse.seen = true;
          mouse.x = mouse.tx;
          mouse.y = mouse.ty;
        }
        camRotTarget.x = mouse.ty * 0.06;
        camRotTarget.y = -mouse.tx * 0.09;
        pushTrail(e.clientX, e.clientY);
      },
      { passive: true }
    );

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(resize, 140);
    });

    // Kart tıklama: kameranın önündeki kart ürün sayfasına götürür.
    root.addEventListener('click', (e) => {
      if (!e.target.closest('[data-cine-stage]')) return;
      let best = null;
      cards.forEach((c) => {
        const d = Math.abs(scroll.current - c.p);
        if (d < 0.03 && (!best || d < best.d)) best = { d, href: c.href };
      });
      if (best && best.href) window.location.href = best.href;
    });

    disposeVisibility = utils.observeVisibility(root, (visible) => {
      running = visible;
      if (visible) loop();
    });
  }

  // ---------------------------------------------------------------------
  // Başlatma
  // ---------------------------------------------------------------------
  function start() {
    if (started) return;
    started = true;
    root.classList.remove('cine--loading');
    root.classList.add('cine--live');
    clockStart = performance.now();
    introStart = clockStart;
    loop();
  }

  function boot() {
    const fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();

    utils
      .loadScript(root.dataset.threeSrc)
      .then(() => fontsReady)
      .then(() => {
        build();
        bindEvents();
        root.classList.add('cine--ready');
        if (enterBtn) {
          enterBtn.addEventListener('click', start, { once: true });
        } else {
          start();
        }
      })
      .catch((err) => {
        console.error('cinematic-intro:', err);
        root.classList.remove('cine--enhanced', 'cine--loading');
        root.classList.add('cine--static');
      });
  }

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    boot();
  } else {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  }

  window.addEventListener('pagehide', () => {
    running = false;
    if (disposeVisibility) disposeVisibility();
  });
})();
